import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import InvitePreview, { CardFace, EnvelopeView, DetailsFace } from '@/components/InvitePreview';
import { invitesApi, money } from '@/lib/invitesApi';
import {
  MagnifyingGlass, Lock, Users, PaperPlaneRight, Check, X, ArrowRight, ArrowLeft,
  Star, TextT, Image as ImageIcon, ImageSquare, MagicWand, EnvelopeSimple, MusicNotes, UploadSimple, Trash,
  CalendarCheck, Plus, TextAlignLeft, TextAlignCenter, TextAlignRight, CaretDown,
  SpinnerGap, FilmSlate, VideoCamera, Palette, Sticker, Shuffle, CaretUp, QrCode,
} from '@phosphor-icons/react';

// ── Design assets (mirror the backend catalog) ──
const PALETTES = [
  { key: 'blush', name: 'Blush', bg: '#FBEFF1', ink: '#3A2A2E', accent: '#D98A9E', liner: '#F3D6DC', envelope: '#F7E6E9' },
  { key: 'sage', name: 'Sage', bg: '#EEF3EC', ink: '#26332A', accent: '#7A9B78', liner: '#D8E5D3', envelope: '#E7EFE3' },
  { key: 'midnight', name: 'Midnight', bg: '#101B2D', ink: '#EAF1FD', accent: '#6B9BFF', liner: '#1C2A44', envelope: '#16233A' },
  { key: 'gold', name: 'Gold', bg: '#FBF7EE', ink: '#3A3220', accent: '#C6A24B', liner: '#EFE2C4', envelope: '#F5ECD8' },
  { key: 'coral', name: 'Coral', bg: '#FFF3F0', ink: '#3B241F', accent: '#FF6D57', liner: '#FFD9D0', envelope: '#FFE7E1' },
  { key: 'royal', name: 'Royal', bg: '#EAF1FD', ink: '#0F1E3D', accent: '#2563EB', liner: '#CFE0FB', envelope: '#DDE9FC' },
  { key: 'plum', name: 'Plum', bg: '#F3EEF6', ink: '#2E1F38', accent: '#8B5C9E', liner: '#E1D2EA', envelope: '#EBE0F1' },
  { key: 'emerald', name: 'Emerald', bg: '#E9F5EF', ink: '#0E2C22', accent: '#0F9D6B', liner: '#CDE9DD', envelope: '#DCF0E7' },
  { key: 'noir', name: 'Noir', bg: '#17171A', ink: '#F2F2F0', accent: '#C9A24B', liner: '#26262B', envelope: '#1F1F23' },
  { key: 'berry', name: 'Berry', bg: '#FBEDF2', ink: '#3A1526', accent: '#C0396B', liner: '#F2CBD9', envelope: '#F7DCE6' },
  // ── 2026 trend palettes (from design-trend research) — richer, warmer, confident.
  { key: 'mocha', name: 'Mocha', trend: true, bg: '#F5F0E8', ink: '#3A2C1E', accent: '#6B4F3A', accent2: '#E8C99A', liner: '#E9DECB', envelope: '#F0E7D6' },
  { key: 'midnight-lux', name: 'Midnight Lux', trend: true, bg: '#141E33', ink: '#F5F0E8', accent: '#C9A84C', accent2: '#7B9CB5', liner: '#1F2C48', envelope: '#18233B' },
  { key: 'terracotta', name: 'Terracotta', trend: true, bg: '#F2EDDF', ink: '#4A2617', accent: '#C4663A', accent2: '#A04B2A', liner: '#E8DBC5', envelope: '#EFE6D2' },
  { key: 'forest-copper', name: 'Forest & Copper', trend: true, bg: '#F0EBD8', ink: '#233527', accent: '#2D4A38', accent2: '#B5703A', liner: '#DDE3CB', envelope: '#E8E7CE' },
  { key: 'butter', name: 'Butter & Sage', trend: true, bg: '#FBF6E4', ink: '#3D3B2A', accent: '#9BAD94', accent2: '#F2D5CC', liner: '#F0E7C4', envelope: '#F7F0DA' },
  { key: 'plum-burgundy', name: 'Plum & Burgundy', trend: true, bg: '#F6EEF0', ink: '#3A1230', accent: '#7C2036', accent2: '#C8967A', liner: '#EAD6DA', envelope: '#F1E1E4' },
  { key: 'lavender', name: 'Lavender', trend: true, bg: '#F6F2FA', ink: '#2E2540', accent: '#8B7BB0', accent2: '#D4C08A', liner: '#E6DDF0', envelope: '#EFE8F5' },
  { key: 'coral-peach', name: 'Coral & Peach', trend: true, bg: '#FFF4EC', ink: '#4A2A22', accent: '#E8735A', accent2: '#F2B89A', liner: '#FBE0D0', envelope: '#FDEBDF' },
  { key: 'black-gold', name: 'Black & Gold', trend: true, bg: '#1A1A1A', ink: '#F5F0E8', accent: '#C9A84C', accent2: '#D8B65C', liner: '#262620', envelope: '#201F1B' },
  { key: 'dusty-blue', name: 'Dusty Blue', trend: true, bg: '#F5F6F4', ink: '#2B3742', accent: '#7B9CB5', accent2: '#B0B8C4', liner: '#E4E8EC', envelope: '#EDF0F2' },
];
// ── Font library — a big, categorized set of Google + system fonts.
// `g` = Google Fonts family (omit for system fonts already available).
const FONT_LIBRARY = [
  // Serif / elegant
  { name: 'Playfair Display', cat: 'Serif', g: 'Playfair Display', family: "'Playfair Display', serif" },
  { name: 'Cormorant Garamond', cat: 'Serif', g: 'Cormorant Garamond', family: "'Cormorant Garamond', serif" },
  { name: 'EB Garamond', cat: 'Serif', g: 'EB Garamond', family: "'EB Garamond', serif" },
  { name: 'Libre Baskerville', cat: 'Serif', g: 'Libre Baskerville', family: "'Libre Baskerville', serif" },
  { name: 'Lora', cat: 'Serif', g: 'Lora', family: "'Lora', serif" },
  { name: 'Merriweather', cat: 'Serif', g: 'Merriweather', family: "'Merriweather', serif" },
  { name: 'Cardo', cat: 'Serif', g: 'Cardo', family: "'Cardo', serif" },
  { name: 'Crimson Text', cat: 'Serif', g: 'Crimson Text', family: "'Crimson Text', serif" },
  { name: 'DM Serif Display', cat: 'Serif', g: 'DM Serif Display', family: "'DM Serif Display', serif" },
  { name: 'Bodoni Moda', cat: 'Serif', g: 'Bodoni Moda', family: "'Bodoni Moda', serif" },
  { name: 'Spectral', cat: 'Serif', g: 'Spectral', family: "'Spectral', serif" },
  { name: 'Prata', cat: 'Serif', g: 'Prata', family: "'Prata', serif" },
  { name: 'PT Serif', cat: 'Serif', g: 'PT Serif', family: "'PT Serif', serif" },
  { name: 'Cormorant', cat: 'Serif', g: 'Cormorant', family: "'Cormorant', serif" },
  { name: 'Georgia', cat: 'Serif', family: "Georgia, 'Times New Roman', serif" },
  { name: 'Palatino', cat: 'Serif', family: "'Palatino Linotype', Palatino, serif" },
  // Sans / modern
  { name: 'Inter', cat: 'Sans', g: 'Inter', family: "'Inter', sans-serif" },
  { name: 'Poppins', cat: 'Sans', g: 'Poppins', family: "'Poppins', sans-serif" },
  { name: 'Montserrat', cat: 'Sans', g: 'Montserrat', family: "'Montserrat', sans-serif" },
  { name: 'Raleway', cat: 'Sans', g: 'Raleway', family: "'Raleway', sans-serif" },
  { name: 'Work Sans', cat: 'Sans', g: 'Work Sans', family: "'Work Sans', sans-serif" },
  { name: 'DM Sans', cat: 'Sans', g: 'DM Sans', family: "'DM Sans', sans-serif" },
  { name: 'Jost', cat: 'Sans', g: 'Jost', family: "'Jost', sans-serif" },
  { name: 'Nunito', cat: 'Sans', g: 'Nunito', family: "'Nunito', sans-serif" },
  { name: 'Quicksand', cat: 'Sans', g: 'Quicksand', family: "'Quicksand', sans-serif" },
  { name: 'Josefin Sans', cat: 'Sans', g: 'Josefin Sans', family: "'Josefin Sans', sans-serif" },
  { name: 'Archivo', cat: 'Sans', g: 'Archivo', family: "'Archivo', sans-serif" },
  { name: 'Lexend', cat: 'Sans', g: 'Lexend', family: "'Lexend', sans-serif" },
  { name: 'Mulish', cat: 'Sans', g: 'Mulish', family: "'Mulish', sans-serif" },
  { name: 'Rubik', cat: 'Sans', g: 'Rubik', family: "'Rubik', sans-serif" },
  { name: 'Karla', cat: 'Sans', g: 'Karla', family: "'Karla', sans-serif" },
  { name: 'Outfit', cat: 'Sans', g: 'Outfit', family: "'Outfit', sans-serif" },
  { name: 'Manrope', cat: 'Sans', g: 'Manrope', family: "'Manrope', sans-serif" },
  { name: 'Trebuchet', cat: 'Sans', family: "'Trebuchet MS', sans-serif" },
  // Script / handwriting
  { name: 'Dancing Script', cat: 'Script', g: 'Dancing Script', family: "'Dancing Script', cursive" },
  { name: 'Great Vibes', cat: 'Script', g: 'Great Vibes', family: "'Great Vibes', cursive" },
  { name: 'Sacramento', cat: 'Script', g: 'Sacramento', family: "'Sacramento', cursive" },
  { name: 'Parisienne', cat: 'Script', g: 'Parisienne', family: "'Parisienne', cursive" },
  { name: 'Allura', cat: 'Script', g: 'Allura', family: "'Allura', cursive" },
  { name: 'Pinyon Script', cat: 'Script', g: 'Pinyon Script', family: "'Pinyon Script', cursive" },
  { name: 'Tangerine', cat: 'Script', g: 'Tangerine', family: "'Tangerine', cursive" },
  { name: 'Satisfy', cat: 'Script', g: 'Satisfy', family: "'Satisfy', cursive" },
  { name: 'Pacifico', cat: 'Script', g: 'Pacifico', family: "'Pacifico', cursive" },
  { name: 'Kaushan Script', cat: 'Script', g: 'Kaushan Script', family: "'Kaushan Script', cursive" },
  { name: 'Yellowtail', cat: 'Script', g: 'Yellowtail', family: "'Yellowtail', cursive" },
  { name: 'Alex Brush', cat: 'Script', g: 'Alex Brush', family: "'Alex Brush', cursive" },
  { name: 'Marck Script', cat: 'Script', g: 'Marck Script', family: "'Marck Script', cursive" },
  { name: 'Caveat', cat: 'Script', g: 'Caveat', family: "'Caveat', cursive" },
  { name: 'Shadows Into Light', cat: 'Script', g: 'Shadows Into Light', family: "'Shadows Into Light', cursive" },
  { name: 'Cookie', cat: 'Script', g: 'Cookie', family: "'Cookie', cursive" },
  { name: 'Courgette', cat: 'Script', g: 'Courgette', family: "'Courgette', cursive" },
  // Display / statement
  { name: 'Abril Fatface', cat: 'Display', g: 'Abril Fatface', family: "'Abril Fatface', serif" },
  { name: 'Bebas Neue', cat: 'Display', g: 'Bebas Neue', family: "'Bebas Neue', sans-serif" },
  { name: 'Anton', cat: 'Display', g: 'Anton', family: "'Anton', sans-serif" },
  { name: 'Cinzel', cat: 'Display', g: 'Cinzel', family: "'Cinzel', serif" },
  { name: 'Cinzel Decorative', cat: 'Display', g: 'Cinzel Decorative', family: "'Cinzel Decorative', serif" },
  { name: 'Oswald', cat: 'Display', g: 'Oswald', family: "'Oswald', sans-serif" },
  { name: 'Fjalla One', cat: 'Display', g: 'Fjalla One', family: "'Fjalla One', sans-serif" },
  { name: 'Alfa Slab One', cat: 'Display', g: 'Alfa Slab One', family: "'Alfa Slab One', serif" },
  { name: 'Lobster', cat: 'Display', g: 'Lobster', family: "'Lobster', cursive" },
  { name: 'Righteous', cat: 'Display', g: 'Righteous', family: "'Righteous', sans-serif" },
  { name: 'Monoton', cat: 'Display', g: 'Monoton', family: "'Monoton', cursive" },
  { name: 'Staatliches', cat: 'Display', g: 'Staatliches', family: "'Staatliches', sans-serif" },
  { name: 'Passion One', cat: 'Display', g: 'Passion One', family: "'Passion One', sans-serif" },
  // Fun / retro / rounded
  { name: 'Fredoka', cat: 'Fun', g: 'Fredoka', family: "'Fredoka', sans-serif" },
  { name: 'Comfortaa', cat: 'Fun', g: 'Comfortaa', family: "'Comfortaa', sans-serif" },
  { name: 'Baloo 2', cat: 'Fun', g: 'Baloo 2', family: "'Baloo 2', sans-serif" },
  { name: 'Amatic SC', cat: 'Fun', g: 'Amatic SC', family: "'Amatic SC', sans-serif" },
  { name: 'Permanent Marker', cat: 'Fun', g: 'Permanent Marker', family: "'Permanent Marker', cursive" },
  { name: 'Gochi Hand', cat: 'Fun', g: 'Gochi Hand', family: "'Gochi Hand', cursive" },
  { name: 'Chewy', cat: 'Fun', g: 'Chewy', family: "'Chewy', cursive" },
  { name: 'Luckiest Guy', cat: 'Fun', g: 'Luckiest Guy', family: "'Luckiest Guy', cursive" },
  { name: 'Bangers', cat: 'Fun', g: 'Bangers', family: "'Bangers', cursive" },
  { name: 'Special Elite', cat: 'Fun', g: 'Special Elite', family: "'Special Elite', cursive" },
  { name: 'Rye', cat: 'Fun', g: 'Rye', family: "'Rye', cursive" },
  // Mono / typewriter
  { name: 'Space Mono', cat: 'Mono', g: 'Space Mono', family: "'Space Mono', monospace" },
  { name: 'IBM Plex Mono', cat: 'Mono', g: 'IBM Plex Mono', family: "'IBM Plex Mono', monospace" },
  { name: 'JetBrains Mono', cat: 'Mono', g: 'JetBrains Mono', family: "'JetBrains Mono', monospace" },
  { name: 'Courier Prime', cat: 'Mono', g: 'Courier Prime', family: "'Courier Prime', monospace" },
  { name: 'Courier', cat: 'Mono', family: "'Courier New', monospace" },
];
const FONT_CATS = ['Serif', 'Sans', 'Script', 'Display', 'Fun', 'Mono'];
// Legacy alias (a few older refs used FONTS)
const FONTS = FONT_LIBRARY;

// Inject all Google Fonts once, the first time a font picker opens.
let _fontsInjected = false;
function loadGoogleFonts() {
  if (_fontsInjected || typeof document === 'undefined') return;
  _fontsInjected = true;
  const fams = FONT_LIBRARY.filter((f) => f.g).map((f) => `family=${f.g.replace(/ /g, '+')}`);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${fams.join('&')}&display=swap`;
  document.head.appendChild(link);
}

// ── Color system — HSV ⇄ hex for the interactive color wheel. ──
function hsvToHex(h, s, v) {
  s /= 100; v /= 100;
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  const to = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}
function hexToHsv(hex) {
  let s = (hex || '').replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return { h: 221, s: 72, v: 92 };
  const r = parseInt(s.slice(0, 2), 16) / 255, g = parseInt(s.slice(2, 4), 16) / 255, b = parseInt(s.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) { if (max === r) h = ((g - b) / d) % 6; else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4; h = h * 60; if (h < 0) h += 360; }
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 };
}
const ENVELOPE_COLORS = ['#F7E6E9', '#FFE7E1', '#DDE9FC', '#E7EFE3', '#F5ECD8', '#EBE0F1', '#DCF0E7', '#F7DCE6', '#16233A', '#1F1F23', '#C0396B', '#7A9B78'];
const LINER_COLORS = ['#F3D6DC', '#FFD9D0', '#CFE0FB', '#D8E5D3', '#EFE2C4', '#E1D2EA', '#CDE9DD', '#F2CBD9', '#1C2A44', '#26262B', '#8B5C9E', '#6B9BFF'];
const STAMPS = ['💌', '💍', '🎂', '🎉', '🌸', '✨', '🎈', '🥂', '🌿', '❤️', '🎓', '🍾', '🦋', '⭐'];
const REVEALS = [
  { key: 'none', label: 'None', premium: false }, { key: 'confetti', label: 'Confetti', premium: true },
  { key: 'reveal', label: 'Petals', premium: true }, { key: 'bloom', label: 'Bloom', premium: true },
  { key: 'sparkle', label: 'Sparkle', premium: true }, { key: 'hearts', label: 'Hearts', premium: true },
  { key: 'shimmer', label: 'Shimmer', premium: true },
];
// Envelope-opening transitions the host can choose. Each re-choreographs the 3D
// stage (index.css .inv-fx-*); the emoji is just a friendly label glyph.
const TRANSITIONS = [
  { key: 'flap-unseal',    label: 'Classic Flap',  emoji: '✉️', desc: 'Seal breaks, flap swings open, card lifts out' },
  { key: 'wax-seal',       label: 'Wax Seal',      emoji: '🕯️', desc: 'A wax seal cracks in two and falls away' },
  { key: 'card-slide',     label: 'Slide Out',     emoji: '⬆️', desc: 'The card glides up out of the envelope' },
  { key: 'fold-out',       label: 'Fold Open',     emoji: '📖', desc: 'The card turns to face you like a card opening' },
  { key: 'confetti-burst', label: 'Confetti',      emoji: '🎉', desc: 'Confetti bursts as the card appears' },
  { key: 'petal-fall',     label: 'Petal Fall',    emoji: '🌸', desc: 'Soft petals drift down over the card' },
  { key: 'lights-flicker', label: 'String Lights', emoji: '💡', desc: 'Warm bulbs flicker on around the card' },
  { key: 'ink-bloom',      label: 'Ink Bloom',     emoji: '🌀', desc: 'The card rises from an expanding wash' },
  { key: 'curtain-reveal', label: 'Curtain',       emoji: '🎭', desc: 'Two drapes part to reveal the card' },
  { key: 'foil-sweep',     label: 'Foil Shimmer',  emoji: '✨', desc: 'A gold-foil highlight sweeps across the card' },
];
// Curated, royalty-free tracks (SoundHelix) — actually play when the invite opens.
const SONG_LIBRARY = [
  { title: 'Golden Hour', artist: 'Bookr Sounds', mood: 'warm & romantic', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' },
  { title: 'Neon Nights', artist: 'Bookr Sounds', mood: 'upbeat pop', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
  { title: 'Poolside', artist: 'Bookr Sounds', mood: 'tropical house', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3' },
  { title: 'Celebration', artist: 'Bookr Sounds', mood: 'festive & fun', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3' },
  { title: 'First Dance', artist: 'Bookr Sounds', mood: 'slow & dreamy', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
  { title: 'After Hours', artist: 'Bookr Sounds', mood: 'moody & cool', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3' },
];
const BG_PRESETS = [
  { type: 'solid', color: '#FBEFF1' }, { type: 'solid', color: '#EAF1FD' }, { type: 'solid', color: '#EEF3EC' },
  { type: 'solid', color: '#101B2D' }, { type: 'solid', color: '#17171A' }, { type: 'solid', color: '#FFF3F0' },
  { type: 'gradient', from: '#FBEFF1', to: '#D98A9E' }, { type: 'gradient', from: '#EAF1FD', to: '#2563EB' },
  { type: 'gradient', from: '#E9F5EF', to: '#0F9D6B' }, { type: 'gradient', from: '#F3EEF6', to: '#8B5C9E' },
];

// Card style the design renders as. Classic (card/flyer) + creative formats.
const FORMATS = [
  { key: 'card', label: 'Card', emoji: '💌' },
  { key: 'flyer', label: 'Flyer', emoji: '📄' },
  { key: 'nowplaying', label: 'Now Playing', emoji: '🎵', hint: 'Best with a photo + song attached.' },
  { key: 'vinyl', label: 'Vinyl', emoji: '🎶' },
  { key: 'boarding', label: 'Boarding Pass', emoji: '✈️' },
  { key: 'polaroid', label: 'Polaroid', emoji: '📸', hint: 'Add a photo for the snapshot.' },
  { key: 'cassette', label: 'Cassette', emoji: '📼' },
];

const STEPS = [
  { key: 'design', label: 'Design' }, { key: 'details', label: 'Details' },
  { key: 'guests', label: 'Guests' }, { key: 'review', label: 'Review & Send' },
];
const TOOLS = [
  { key: 'text', label: 'Text', icon: TextT },
  { key: 'palette', label: 'Palette', icon: Palette },
  { key: 'stickers', label: 'Stickers', icon: Sticker },
  { key: 'background', label: 'Backgrounds', icon: ImageIcon },
  { key: 'media', label: 'Photo & Video', icon: ImageSquare },
  { key: 'music', label: 'Music', icon: MusicNotes },
  { key: 'effects', label: 'Effects', icon: MagicWand },
  { key: 'envelope', label: 'Envelope', icon: EnvelopeSimple },
  { key: 'opening', label: 'Opening', icon: FilmSlate },
];

const field = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';
const label = 'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 block';

function designFromTemplate(t) {
  const L = t.layout || {};
  return {
    background: { ...(L.background || { type: 'solid', color: t.colors?.bg }) },
    motif: L.motif || 'none', colors: { ...t.colors }, font: { ...t.font },
    blocks: (L.blocks || []).map((b) => ({ ...b })),
    stamp: { ...(L.stamp || {}) }, liner: { ...(L.liner || {}) }, envelope: { ...(L.envelope || {}) },
    // Carry the full design so the builder keeps a template's decor, illustrated
    // scene, occasion graphics, text inset and opening effect (previously dropped).
    decor: { ...(L.decor || {}) }, layout: L.layout || 'centered',
    scene: L.scene || null, sceneColors: L.sceneColors ? { ...L.sceneColors } : null,
    occasionArt: L.occasionArt || null, textInset: L.textInset,
    openFx: L.openFx || null, stickers: [],
    backdrop: null, animation: L.envelope?.animation || 'none', music: null, photo: null,
  };
}
function applyEventToBlocks(design, ev) {
  if (!ev) return design;
  design.blocks = design.blocks.map((b) => {
    if (b.id === 'title' && ev.title) return { ...b, text: ev.title };
    if (b.id === 'date' && (ev.event_date || ev.start_time)) return { ...b, text: [ev.event_date, ev.start_time].filter(Boolean).join(' · ') };
    if (b.id === 'location' && ev.location) return { ...b, text: ev.location };
    return b;
  });
  return design;
}

// On-brand dropdown (replaces the native OS <select> popup).
function Select({ value, onChange, options, className, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors cursor-pointer">
        <span className="truncate">{current?.label}</span>
        <CaretDown size={13} weight="bold" className={`text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1.5 bg-card border border-border/70 rounded-xl shadow-lift py-1 z-50 fade-up" role="listbox">
          {options.map((o) => (
            <button key={o.value} type="button" role="option" aria-selected={o.value === value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-secondary/60 transition-colors cursor-pointer ${o.value === value ? 'text-primary font-medium' : 'text-foreground'}`}>
              {o.label} {o.value === value && <Check size={13} className="text-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Interactive color wheel (drag hue+saturation) + brightness slider ──
function ColorWheel({ value, onChange, size = 172 }) {
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const wheelRef = useRef(null);
  const dragging = useRef(false);
  const lastEmit = useRef(null);
  // Sync from external changes (preset/neutral/hex) but ignore our own emissions.
  useEffect(() => {
    if (!/^#([0-9a-fA-F]{6})$/.test(value || '')) return;
    if ((value || '').toUpperCase() === (lastEmit.current || '').toUpperCase()) return;
    setHsv(hexToHsv(value));
  }, [value]);
  const emit = (h, s, v) => { const c = hsvToHex(h, s, v); lastEmit.current = c; onChange(c); };
  const pick = (e) => {
    const el = wheelRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2, cy = rect.height / 2, R = rect.width / 2;
    const px = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left - cx;
    const py = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top - cy;
    const s = Math.min(1, Math.hypot(px, py) / R) * 100;
    const h = (Math.atan2(px, -py) * 180 / Math.PI + 360) % 360;
    setHsv((prev) => { emit(h, s, prev.v); return { h, s, v: prev.v }; });
  };
  useEffect(() => {
    const move = (e) => { if (dragging.current) { e.preventDefault(); pick(e); } };
    const up = () => { dragging.current = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const R = size / 2;
  const rr = (hsv.s / 100) * R;
  const rad = hsv.h * Math.PI / 180;
  const knobX = R + rr * Math.sin(rad);
  const knobY = R - rr * Math.cos(rad);
  const cur = hsvToHex(hsv.h, hsv.s, hsv.v);
  return (
    <div className="flex flex-col items-center gap-3">
      <div ref={wheelRef}
        onMouseDown={(e) => { dragging.current = true; pick(e); }}
        onTouchStart={(e) => { dragging.current = true; pick(e); }}
        className="relative rounded-full cursor-crosshair select-none touch-none"
        style={{
          width: size, height: size,
          background: `radial-gradient(circle at center, #fff 0%, rgba(255,255,255,0) 100%), conic-gradient(from 0deg, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))`,
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.10)',
        }}>
        <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: '#000', opacity: 1 - hsv.v / 100 }} />
        <div className="absolute w-4 h-4 rounded-full border-2 border-white pointer-events-none"
          style={{ left: knobX, top: knobY, transform: 'translate(-50%, -50%)', background: cur, boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.45)' }} />
      </div>
      <div className="w-full flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg border border-black/10 shrink-0" style={{ background: cur }} />
        <input type="range" min="0" max="100" value={Math.round(hsv.v)} aria-label="Brightness"
          onChange={(e) => { const v = +e.target.value; setHsv((prev) => { emit(prev.h, prev.s, v); return { ...prev, v }; }); }}
          className="flex-1 h-2 rounded-full appearance-none cursor-pointer"
          style={{ accentColor: hsvToHex(hsv.h, hsv.s, 100), background: `linear-gradient(90deg, #000, ${hsvToHex(hsv.h, hsv.s, 100)})` }} />
      </div>
    </div>
  );
}

// ── Color picker — just the wheel (inline-expanding so it never clips in a scroll panel) ──
function ColorPicker({ value, onChange, allowClear, onClear }) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value || '');
  useEffect(() => { setHex(value || ''); }, [value]);
  const commitHex = (v) => {
    let s = v.trim(); if (s && !s.startsWith('#')) s = '#' + s;
    setHex(s);
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s)) onChange(s.toUpperCase());
  };
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 hover:bg-secondary/40 transition-colors">
        <span className="w-8 h-8 rounded-lg border border-black/10 shrink-0 shadow-inner" style={{ background: value || 'transparent', backgroundImage: value ? 'none' : 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 12px 12px' }} />
        <span className="flex-1 text-left min-w-0">
          <span className="block text-[13px] font-medium text-foreground">{value ? value.toUpperCase() : 'Pick a color'}</span>
          <span className="block text-[10px] text-muted-foreground">Color wheel</span>
        </span>
        <CaretDown size={13} weight="bold" className={`text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="p-3 border-t border-border bg-card/60 space-y-3 tab-fade">
          <ColorWheel value={value} onChange={onChange} />
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center rounded-lg border border-border h-9 px-2 focus-within:ring-2 focus-within:ring-primary/40">
              <span className="text-xs text-muted-foreground">#</span>
              <input value={(hex || '').replace(/^#/, '')} onChange={(e) => commitHex(e.target.value)} placeholder="HEX"
                className="w-full bg-transparent text-xs text-foreground focus:outline-none uppercase" maxLength={7} />
            </div>
            {allowClear && value && (
              <button type="button" onClick={() => { onClear ? onClear() : onChange(''); }} className="text-[11px] text-muted-foreground hover:text-accent-coral shrink-0">Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Font picker with live previews, category tabs & search (loads Google Fonts on open) ──
function FontPicker({ value, onChange }) {
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  useEffect(() => { loadGoogleFonts(); }, []);
  const list = FONT_LIBRARY.filter((f) => (cat === 'All' || f.cat === cat) && (!q || f.name.toLowerCase().includes(q.toLowerCase())));
  const current = FONT_LIBRARY.find((f) => f.family === value);
  return (
    <div className="space-y-2">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${FONT_LIBRARY.length} fonts…`} className={field + ' pl-8'} />
      </div>
      <div className="flex flex-wrap gap-1">
        {['All', ...FONT_CATS].map((c) => (
          <button key={c} type="button" onClick={() => setCat(c)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${cat === c ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-secondary/50'}`}>{c}</button>
        ))}
      </div>
      <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border/60">
        <button type="button" onClick={() => onChange('')} className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/40 ${!value ? 'bg-primary/5' : ''}`}>
          <span className="text-sm text-foreground">Default (template font)</span>{!value && <Check size={14} className="text-primary" />}
        </button>
        {list.map((f) => (
          <button key={f.name} type="button" onClick={() => onChange(f.family)} className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/40 ${value === f.family ? 'bg-primary/5' : ''}`}>
            <span className="min-w-0">
              <span className="block text-[17px] leading-tight text-foreground truncate" style={{ fontFamily: f.family }}>{f.name}</span>
              <span className="block text-[9px] uppercase tracking-wide text-muted-foreground">{f.cat}</span>
            </span>
            {value === f.family && <Check size={15} className="text-primary shrink-0" />}
          </button>
        ))}
        {!list.length && <p className="text-xs text-muted-foreground text-center py-6">No fonts match “{q}”.</p>}
      </div>
      {current && <p className="text-[10px] text-muted-foreground">Selected: <span style={{ fontFamily: current.family }}>{current.name}</span></p>}
    </div>
  );
}

// ── Drag-and-drop upload zone (photos & video) → real file upload ──
function DropZone({ accept, kind, token, onUploaded, flash, compact }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const inputRef = useRef(null);
  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    for (const file of list) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      if (kind === 'photo' && !isImage) { flash?.('That’s not an image file.'); continue; }
      if (kind === 'video' && !isVideo) { flash?.('That’s not a video file.'); continue; }
      const cap = isVideo ? 40 : 12;
      if (file.size > cap * 1024 * 1024) { flash?.(`File too large — keep ${kind}s under ${cap}MB.`); continue; }
      setBusy(true); setPct(0);
      try {
        const url = await invitesApi.upload(file, token, setPct);
        onUploaded(url, isVideo ? 'video' : 'photo');
      } catch (e) {
        flash?.(e?.response?.status === 401 ? 'Please sign in again to upload.' : 'Upload failed — try again.');
      } finally { setBusy(false); setPct(0); }
    }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
      onClick={() => !busy && inputRef.current?.click()}
      role="button" tabIndex={0}
      className={`relative w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${compact ? 'py-4 px-3' : 'py-7 px-4'} ${drag ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-secondary/30'}`}
      data-testid={`dropzone-${kind}`}>
      <input ref={inputRef} type="file" accept={accept} multiple={kind === 'photo'} className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      {busy ? (
        <>
          <SpinnerGap size={22} className="text-primary animate-spin mb-1.5" />
          <span className="text-xs font-semibold text-foreground">Uploading… {pct}%</span>
          <div className="w-28 h-1.5 rounded-full bg-secondary mt-2 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
        </>
      ) : (
        <>
          <span className={`rounded-full bg-primary/10 text-primary flex items-center justify-center mb-1.5 ${compact ? 'w-8 h-8' : 'w-11 h-11'}`}>
            {kind === 'video' ? <VideoCamera size={compact ? 16 : 20} weight="fill" /> : <UploadSimple size={compact ? 16 : 20} weight="bold" />}
          </span>
          <span className="text-[13px] font-semibold text-foreground">{drag ? 'Drop to upload' : `Drag & drop a ${kind}`}</span>
          <span className="text-[11px] text-muted-foreground mt-0.5">or <span className="text-primary font-semibold">browse files</span></span>
        </>
      )}
    </div>
  );
}

// ══════════════ GALLERY ══════════════
function TemplateCard({ t, onPick }) {
  const design = designFromTemplate(t);
  return (
    <button onClick={() => onPick(t)} className="group text-left bg-card rounded-xl border border-border/70 shadow-soft overflow-hidden card-lift cursor-pointer" data-testid="template-card">
      <div className="relative flex items-center justify-center bg-secondary/40 p-4" style={{ minHeight: 210 }}>
        {/* Real design thumbnail — motif art, colors, copy & font */}
        <CardFace design={design} scale={132} format={t.format} />
        {t.premium && <span className="absolute top-2 left-2 z-10 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-foreground/80 text-white px-1.5 py-0.5 rounded-full"><Star size={9} weight="fill" /> Premium</span>}
        <span className="absolute bottom-2 right-2 z-10 text-[9px] font-semibold uppercase bg-white/85 text-foreground px-1.5 py-0.5 rounded-full">{t.format}</span>
      </div>
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-medium text-foreground truncate">{t.name}</span>
        <span className="text-[10px] font-semibold text-primary opacity-0 group-hover:opacity-100 shrink-0 ml-2">Use →</span>
      </div>
    </button>
  );
}

// Conversational refine bar under the canvas — talk to the AI to tweak the design.
function RefineBar({ onRemix, flash }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const CHIPS = ['More colorful', 'Make it fancier', 'Funnier copy', 'Darker & moodier', 'More minimal'];
  const run = async (instruction) => {
    const t = (instruction ?? text).trim();
    if (t.length < 2 || busy) return;
    setBusy(true);
    try { await onRemix(t); setText(''); }
    catch (e) { flash?.(e?.response?.data?.detail || 'Couldn’t apply that — try rephrasing.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full max-w-[560px] mt-4" data-testid="refine-bar">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagicWand size={15} weight="fill" className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            disabled={busy} placeholder="Tell the AI a tweak — “make it more elegant”, “try teal”…"
            className={`${field} pl-9`} data-testid="refine-input" />
        </div>
        <button onClick={() => run()} disabled={busy || text.trim().length < 2}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 h-10 text-sm font-semibold disabled:opacity-60" data-testid="refine-go">
          {busy ? <SpinnerGap size={15} className="animate-spin" /> : 'Refine'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
        {CHIPS.map((c) => (
          <button key={c} onClick={() => run(c)} disabled={busy}
            className="text-[11px] text-muted-foreground border border-border rounded-full px-2.5 py-1 hover:border-primary/40 hover:text-primary disabled:opacity-50">{c}</button>
        ))}
      </div>
    </div>
  );
}

function GenerateHero({ onGenerate, onGenerateOptions, onChooseOption }) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [options, setOptions] = useState(null);   // 3 AI directions to choose from
  const [choosing, setChoosing] = useState(-1);
  const EXAMPLES = [
    'Neon 90s roller-disco 30th birthday, purple & teal',
    'Elegant garden wedding, dusty sage & gold',
    'Cozy cabin friendsgiving dinner',
    'Y2K bachelorette in Vegas, hot pink',
  ];
  const go = async () => {
    const p = prompt.trim();
    if (p.length < 3) { setErr('Tell us a bit about your event first.'); return; }
    setErr(''); setBusy(true); setOptions(null);
    try {
      if (onGenerateOptions) {
        const r = await onGenerateOptions(p, 'card');
        setOptions(r.options || []);
      } else {
        await onGenerate(p, 'card');
      }
    } catch (e) { setErr(e?.response?.data?.detail || 'Couldn’t design that — try rephrasing.'); }
    finally { setBusy(false); }
  };
  const pick = async (opt, i) => {
    setChoosing(i);
    try { await onChooseOption(opt, prompt.trim()); }
    catch (e) { setErr(e?.response?.data?.detail || 'Couldn’t use that one — try again.'); setChoosing(-1); }
  };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/8 via-card to-secondary/40 p-5 sm:p-6 mb-6" data-testid="generate-hero">
      <div className="flex items-center gap-2 mb-1.5">
        <MagicWand size={18} weight="fill" className="text-primary" />
        <h3 className="text-base font-display font-semibold text-foreground">Design it with AI — describe your party</h3>
        <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground rounded-full px-2 py-0.5">New</span>
      </div>
      <p className="text-sm text-muted-foreground mb-3">One line about your event and we’ll design three looks to choose from — colors, art, words and all.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) go(); }}
          placeholder="e.g. Neon 90s roller-disco 30th birthday, purple & teal"
          disabled={busy} className={`${field} flex-1`} data-testid="generate-prompt" autoFocus />
        <button onClick={go} disabled={busy}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 h-11 text-sm font-semibold shadow-soft hover:opacity-90 disabled:opacity-60"
          data-testid="generate-btn">
          {busy ? <><SpinnerGap size={16} className="animate-spin" /> Designing…</> : <><MagicWand size={16} weight="fill" /> {options ? 'Try again' : 'Generate'}</>}
        </button>
      </div>
      {err && <p className="text-xs text-accent-coral mt-2" data-testid="generate-error">{err}</p>}
      {busy && <p className="text-xs text-primary mt-2 animate-pulse">Sketching three directions — colors, art, words…</p>}

      {options && options.length > 0 && (
        <div className="mt-4" data-testid="generate-options">
          <p className="text-xs font-semibold text-foreground mb-2">Pick a direction — you can tweak everything after.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {options.map((opt, i) => (
              <button key={i} onClick={() => pick(opt, i)} disabled={choosing >= 0}
                className="group relative text-left rounded-xl border-2 border-border hover:border-primary overflow-hidden bg-card transition-colors disabled:opacity-60"
                data-testid={`generate-option-${i}`}>
                <div className="w-full flex justify-center bg-secondary/40 p-2">
                  <CardFace design={opt.design} scale={150} format={opt.format} />
                </div>
                <div className="px-2.5 py-2 flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold text-foreground truncate">{opt.label}</span>
                  {choosing === i
                    ? <SpinnerGap size={13} className="animate-spin text-primary shrink-0" />
                    : <span className="text-[10px] font-semibold text-primary opacity-0 group-hover:opacity-100 shrink-0">Use →</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!busy && !options && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => setPrompt(ex)} className="text-[11px] text-muted-foreground border border-border rounded-full px-2.5 py-1 hover:border-primary/40 hover:text-primary">{ex}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Gallery({ facets, filters, setFilters, templates, onPick, onGenerate, onGenerateOptions, onChooseOption, onClose, canClose }) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="template-gallery">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-display font-semibold text-foreground">Choose a design</h2>
          <p className="text-sm text-muted-foreground">Design with AI, or pick from hundreds of templates — Cards & Flyers.</p>
        </div>
        {canClose && <button onClick={onClose} className="text-sm font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1"><X size={16} /> Close</button>}
      </div>

      {onGenerate && <GenerateHero onGenerate={onGenerate} onGenerateOptions={onGenerateOptions} onChooseOption={onChooseOption} />}

      {/* Event category chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilters((f) => ({ ...f, category: '' }))} className={`text-sm font-medium px-3.5 py-2 rounded-full border ${!filters.category ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-secondary/60'}`}>All events</button>
        {facets?.categories?.map((c) => (
          <button key={c.key} onClick={() => setFilters((f) => ({ ...f, category: c.key }))} className={`text-sm font-medium px-3.5 py-2 rounded-full border ${filters.category === c.key ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-secondary/60'}`}>{c.name}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search designs…" className={`${field} pl-9`} />
        </div>
        <Select className="w-40" ariaLabel="Format" value={filters.format} onChange={(v) => setFilters((f) => ({ ...f, format: v }))}
          options={[{ value: '', label: 'Card & Flyer' }, { value: 'card', label: 'Card' }, { value: 'flyer', label: 'Flyer' }]} />
        <Select className="w-44" ariaLabel="Price" value={filters.premium} onChange={(v) => setFilters((f) => ({ ...f, premium: v }))}
          options={[{ value: '', label: 'Free & Premium' }, { value: 'false', label: 'Free' }, { value: 'true', label: 'Premium' }]} />
        <div className="flex items-center gap-1.5 pl-1">
          {facets?.colors?.slice(0, 10).map((c) => (
            <button key={c.key} onClick={() => setFilters((f) => ({ ...f, color: f.color === c.key ? '' : c.key }))} title={c.name}
              className={`w-6 h-6 rounded-full border-2 ${filters.color === c.key ? 'border-primary' : 'border-transparent'}`} style={{ background: c.swatch }} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {templates.map((t) => <TemplateCard key={t.template_id} t={t} onPick={onPick} />)}
      </div>
      {!templates.length && <p className="text-center text-sm text-muted-foreground py-16">No designs match those filters.</p>}
    </div>
  );
}

// ══════════════ TOOL PANELS ══════════════
function AlignBtns({ value, onChange }) {
  const opts = [['left', TextAlignLeft], ['center', TextAlignCenter], ['right', TextAlignRight]];
  return (
    <div className="flex gap-1">
      {opts.map(([v, Icon]) => (
        <button key={v} onClick={() => onChange(v)} className={`flex-1 h-9 rounded-lg border flex items-center justify-center ${value === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'}`}><Icon size={16} /></button>
      ))}
    </div>
  );
}

// Add-a-block presets for the Text tool — common invitation lines.
const BLOCK_PRESETS = [
  { label: 'Heading', block: { type: 'heading', text: 'Your Heading', align: 'center', size: 34, casing: 'none' } },
  { label: 'Subtitle', block: { type: 'subheading', text: 'A little subtitle', align: 'center', size: 18 } },
  { label: 'Detail line', block: { type: 'detail', text: 'Saturday · 6:00 PM', align: 'center', size: 14 } },
  { label: 'Dress code', block: { type: 'detail', text: 'Dress code: cocktail attire', align: 'center', size: 13 } },
  { label: 'Schedule', block: { type: 'body', text: '6:00 Cocktails · 7:00 Dinner · 9:00 Dancing', align: 'center', size: 13 } },
  { label: 'Registry', block: { type: 'body', text: 'Registry & gift details inside', align: 'center', size: 13 } },
  { label: 'Bring a dish', block: { type: 'body', text: 'Potluck — bring your favorite dish!', align: 'center', size: 13 } },
  { label: 'Monogram', block: { type: 'heading', text: 'E · O', align: 'center', size: 40, casing: 'upper', letterSpacing: 3 } },
  { label: 'Custom text', block: { type: 'body', text: 'New text', align: 'center', size: 16 } },
];

const STICKER_SETS = {
  Celebrate: ['🎉', '🎊', '🥳', '🎈', '🎁', '🪩', '🍾', '🥂', '🎂', '🧁', '✨', '⭐'],
  Love: ['❤️', '💕', '💖', '💍', '🌹', '💐', '💌', '🕊️', '💞', '🫶', '😍', '💫'],
  Floral: ['🌸', '🌷', '🌼', '🌻', '🌿', '🍃', '🌺', '🪷', '🌾', '🍀', '🌱', '🏵️'],
  Party: ['🕺', '💃', '🎤', '🎶', '🎧', '🍸', '🍹', '🪅', '🎯', '🃏', '🎸', '🪩'],
  Nature: ['☀️', '🌙', '⛰️', '🌊', '🌴', '🏖️', '🔥', '❄️', '🍁', '🦋', '🐝', '🌈'],
  Fun: ['😎', '🤩', '👑', '💎', '⚡', '🏆', '📸', '🥇', '🎀', '🧸', '🍭', '🚀'],
};

function StickersTool({ invitation, patchDesign, selectedStickerId, setSelectedStickerId }) {
  const [cat, setCat] = useState('Celebrate');
  const stickers = invitation.design.stickers || [];
  const sel = stickers.find((s) => s.id === selectedStickerId);
  const add = (glyph) => {
    const id = 's' + Date.now().toString(36);
    patchDesign((d) => { d.stickers = [...(d.stickers || []), { id, glyph, x: 0.5, y: 0.32, size: 42, rot: 0 }]; return d; });
    setSelectedStickerId(id);
  };
  const updSel = (patch) => patchDesign((d) => { d.stickers = (d.stickers || []).map((s) => s.id === selectedStickerId ? { ...s, ...patch } : s); return d; });
  const del = () => { patchDesign((d) => { d.stickers = (d.stickers || []).filter((s) => s.id !== selectedStickerId); return d; }); setSelectedStickerId(null); };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Tap a sticker to drop it on, then drag it anywhere on the card.</p>
      {sel && (
        <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground flex items-center gap-2"><span className="text-lg">{sel.glyph}</span> Selected</span>
            <button onClick={del} className="text-muted-foreground hover:text-accent-coral" data-testid="sticker-delete"><Trash size={15} /></button>
          </div>
          <div><span className={label}>Size</span>
            <input type="range" min="16" max="140" value={sel.size || 42} onChange={(e) => updSel({ size: +e.target.value })} className="w-full" /></div>
          <div><span className={label}>Rotation</span>
            <input type="range" min="-180" max="180" value={sel.rot || 0} onChange={(e) => updSel({ rot: +e.target.value })} className="w-full" /></div>
          <button onClick={() => setSelectedStickerId(null)} className="text-[11px] text-muted-foreground hover:text-foreground">Done</button>
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {Object.keys(STICKER_SETS).map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`px-2.5 py-1 text-[11px] font-semibold rounded-full ${cat === c ? 'bg-primary text-primary-foreground' : 'bg-secondary/60 text-muted-foreground hover:text-foreground'}`}>{c}</button>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {STICKER_SETS[cat].map((g, i) => (
          <button key={i} onClick={() => add(g)} className="h-10 rounded-lg border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-xl" data-testid={`sticker-add-${cat}-${i}`}>{g}</button>
        ))}
      </div>
      {stickers.length > 0 && <p className="text-[11px] text-muted-foreground">{stickers.length} sticker{stickers.length > 1 ? 's' : ''} on your card · tap one on the canvas to edit.</p>}
    </div>
  );
}

// ── Palette helpers ─────────────────────────────────────────────────────────
function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return '#' + [f(0), f(8), f(4)].map((x) => x.toString(16).padStart(2, '0')).join('');
}
// Generate a fresh, coordinated palette around a random base hue.
function generatePalette() {
  const h = Math.floor(Math.random() * 360);
  const dark = Math.random() < 0.28;
  const a2 = (h + (Math.random() < 0.5 ? 150 : 40)) % 360;
  return dark
    ? { bg: hslToHex(h, 30, 12), ink: hslToHex(h, 22, 94), accent: hslToHex(h, 68, 62),
        accent2: hslToHex(a2, 55, 60), liner: hslToHex(h, 28, 20), envelope: hslToHex(h, 26, 16) }
    : { bg: hslToHex(h, 42, 96), ink: hslToHex(h, 34, 16), accent: hslToHex(h, 62, 50),
        accent2: hslToHex(a2, 48, 52), liner: hslToHex(h, 40, 88), envelope: hslToHex(h, 42, 92) };
}
// Pull a coordinated palette out of an uploaded photo (same-origin, so untainted).
function extractPalette(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const S = 64, c = document.createElement('canvas');
        c.width = S; c.height = S;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, S, S);
        const px = ctx.getImageData(0, 0, S, S).data;
        let ar = 0, ag = 0, ab = 0, n = 0, best = null, bestSat = -1, second = null, secondSat = -1;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i], g = px[i + 1], b = px[i + 2];
          ar += r; ag += g; ab += b; n++;
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          const sat = mx === 0 ? 0 : (mx - mn) / mx;
          const hue = Math.atan2(1.732 * (g - b), 2 * r - g - b);
          if (sat > bestSat) { secondSat = bestSat; second = best; bestSat = sat; best = [r, g, b, hue]; }
          else if (sat > secondSat && best && Math.abs(hue - best[3]) > 0.7) { secondSat = sat; second = [r, g, b, hue]; }
        }
        const avg = [ar / n, ag / n, ab / n];
        const mix = (c1, c2, t) => c1.map((v, i) => Math.round(v + (c2[i] - v) * t));
        const hex = (a) => '#' + a.slice(0, 3).map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
        const accent = best || avg;
        const accent2 = second || mix(accent, [255, 255, 255], 0.35);
        resolve({
          bg: hex(mix(avg, [255, 255, 255], 0.86)),
          ink: hex(mix(avg, [0, 0, 0], 0.82)),
          accent: hex(accent),
          accent2: hex(accent2),
          liner: hex(mix(accent, [255, 255, 255], 0.6)),
          envelope: hex(mix(avg, [255, 255, 255], 0.8)),
        });
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = url;
  });
}

function PaletteTool({ invitation, patchDesign, token, flash }) {
  const cur = invitation.design.colors || {};
  const apply = (p) => patchDesign((d) => {
    d.colors = { ...d.colors, bg: p.bg, ink: p.ink, accent: p.accent, accent2: p.accent2 || p.accent };
    if (d.background?.type === 'solid' || !d.background) d.background = { type: 'solid', color: p.bg };
    if (p.liner) d.liner = { ...d.liner, color: p.liner };
    if (p.envelope) d.envelope = { ...d.envelope, color: p.envelope };
    return d;
  });
  const swatch = (c) => <span className="w-5 h-5 rounded-full border border-black/10" style={{ background: c }} />;
  return (
    <div className="space-y-5">
      <div>
        <span className={label}>Current palette</span>
        <div className="flex items-center gap-1.5">
          {[cur.bg, cur.ink, cur.accent, cur.accent2].filter(Boolean).map((c, i) => (
            <span key={i} className="w-5 h-5 rounded-full border border-black/10" style={{ background: c }} />
          ))}
        </div>
      </div>
      <button onClick={() => apply(generatePalette())}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-semibold shadow-soft hover:opacity-90"
        data-testid="palette-shuffle">
        <Shuffle size={16} weight="bold" /> Shuffle a fresh palette
      </button>
      <div>
        <span className={label}>2026 trend palettes ✨</span>
        <div className="grid grid-cols-5 gap-2">
          {PALETTES.filter((p) => p.trend).map((p) => (
            <button key={p.key} title={p.name} onClick={() => apply(p)}
              className="h-12 rounded-lg border-2 border-transparent hover:border-primary overflow-hidden flex flex-col" data-testid={`palette-${p.key}`}>
              <span className="flex-1" style={{ background: p.bg }} />
              <span className="h-3.5 flex">
                <span className="flex-1" style={{ background: p.accent }} />
                <span className="flex-1" style={{ background: p.accent2 || p.accent }} />
                <span className="flex-1" style={{ background: p.ink }} />
              </span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Classic themes</span>
        <div className="grid grid-cols-5 gap-2">
          {PALETTES.filter((p) => !p.trend).map((p) => (
            <button key={p.key} title={p.name} onClick={() => apply(p)}
              className="h-12 rounded-lg border-2 border-transparent hover:border-primary overflow-hidden flex flex-col" data-testid={`palette-${p.key}`}>
              <span className="flex-1" style={{ background: p.bg }} />
              <span className="h-3 flex">
                <span className="flex-1" style={{ background: p.accent }} />
                <span className="flex-1" style={{ background: p.ink }} />
              </span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Steal colors from a photo</span>
        <DropZone kind="photo" accept="image/*" token={token} flash={flash} compact
          onUploaded={(url) => extractPalette(url).then(apply).then(() => flash('Palette pulled from your photo ✨')).catch(() => flash('Couldn’t read that image'))} />
        <p className="text-[11px] text-muted-foreground mt-1.5">Upload any picture — we’ll build a matching color set.</p>
      </div>
    </div>
  );
}

// Ask-the-Host Genie — one tap drafts a few on-voice copy options for a block.
function GenieButton({ kind, context, token, onPick, flash }) {
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(null);
  const ask = async () => {
    setBusy(true); setOpts(null);
    try {
      const r = await invitesApi.assist({ kind, context: context || '' }, token);
      setOpts(r.options || []);
    } catch (e) { flash?.(e?.response?.data?.detail || 'Couldn’t get ideas — try again.'); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <button onClick={ask} disabled={busy} data-testid={`genie-${kind}`}
        className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary py-2 text-xs font-semibold hover:bg-primary/10 disabled:opacity-60">
        {busy ? <><SpinnerGap size={13} className="animate-spin" /> Thinking…</> : <><MagicWand size={13} weight="fill" /> Help me word this</>}
      </button>
      {opts && (
        <div className="mt-2 space-y-1.5" data-testid="genie-options">
          {opts.map((o, i) => (
            <button key={i} onClick={() => { onPick(o); setOpts(null); }}
              className="w-full text-left text-xs rounded-lg border border-border px-2.5 py-2 hover:border-primary/40 hover:bg-secondary/40 leading-snug">
              {o}
            </button>
          ))}
          <button onClick={ask} disabled={busy} className="text-[11px] text-muted-foreground hover:text-primary">↻ More ideas</button>
        </div>
      )}
    </div>
  );
}

function TextTool({ invitation, patchDesign, selectedId, setSelectedId, token, flash }) {
  const blocks = invitation.design.blocks || [];
  const block = blocks.find((b) => b.id === selectedId);
  const upd = (patch) => patchDesign((d) => { d.blocks = d.blocks.map((x) => x.id === selectedId ? { ...x, ...patch } : x); return d; });
  const addBox = () => {
    const id = `t${Date.now().toString(36)}`;
    patchDesign((d) => { d.blocks = [...d.blocks, { id, type: 'body', text: 'New text', align: 'center', size: 16 }]; return d; });
    setSelectedId(id);
  };
  const removeBox = () => { patchDesign((d) => { d.blocks = d.blocks.filter((x) => x.id !== selectedId); return d; }); setSelectedId(null); };
  const move = (id, dir) => patchDesign((d) => {
    const arr = [...d.blocks]; const i = arr.findIndex((x) => x.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]]; d.blocks = arr; return d;
  });
  const addPreset = (p) => {
    const id = `t${Date.now().toString(36)}`;
    patchDesign((d) => { d.blocks = [...d.blocks, { id, ...p.block }]; return d; });
    setSelectedId(id);
  };

  if (!block) {
    return (
      <div>
        <p className="text-sm text-muted-foreground mb-3">Tap any text on your invitation to edit it, reorder with the arrows, or add a new block below.</p>
        <div className="space-y-1.5 mb-4">
          {blocks.map((b, i) => (
            <div key={b.id} className="flex items-center gap-1">
              <button onClick={() => setSelectedId(b.id)} className="flex-1 text-left px-3 py-2 rounded-lg border border-border hover:border-primary/40 hover:bg-secondary/40 text-sm truncate">{b.text || '(empty)'}</button>
              <div className="flex flex-col">
                <button onClick={() => move(b.id, -1)} disabled={i === 0} className="text-muted-foreground hover:text-primary disabled:opacity-30" aria-label="Move up"><CaretUp size={13} /></button>
                <button onClick={() => move(b.id, 1)} disabled={i === blocks.length - 1} className="text-muted-foreground hover:text-primary disabled:opacity-30" aria-label="Move down"><CaretDown size={13} /></button>
              </div>
            </div>
          ))}
        </div>
        <span className={label}>Add a block</span>
        <div className="grid grid-cols-2 gap-1.5">
          {BLOCK_PRESETS.map((p) => (
            <button key={p.label} onClick={() => addPreset(p)} className="flex items-center gap-1.5 border border-dashed border-border rounded-lg py-2 px-2.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40" data-testid={`add-block-${p.label}`}>
              <Plus size={13} /> {p.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between"><span className={label}>Text</span><button onClick={() => setSelectedId(null)} className="text-[11px] text-muted-foreground hover:text-foreground">Done</button></div>
        <textarea value={block.text} rows={2} onChange={(e) => upd({ text: e.target.value })} className={field + ' resize-none'} autoFocus />
        {(() => {
          const kind = { title: 'title', subtitle: 'subtitle', message: 'message' }[block.id]
            || (block.type === 'heading' ? 'title' : block.type === 'subheading' ? 'subtitle' : 'message');
          const ev = invitation.event || {};
          const context = [ev.title, invitation.design?.font?.name, block.text].filter(Boolean).join(' — ');
          return <div className="mt-2"><GenieButton kind={kind} context={context} token={token} flash={flash} onPick={(t) => upd({ text: t })} /></div>;
        })()}
      </div>
      <div>
        <span className={label}>Font · {FONT_LIBRARY.length} to choose from</span>
        <FontPicker value={block.font || ''} onChange={(fam) => upd({ font: fam })} />
      </div>
      <div className="grid grid-cols-2 gap-3 items-start">
        <div><span className={label}>Type size</span>
          <input type="number" min="8" max="120" value={block.size || 16} onChange={(e) => upd({ size: +e.target.value })} className={field} /></div>
        <div><span className={label}>Type color</span>
          <ColorPicker value={block.color || ''} onChange={(c) => upd({ color: c })} allowClear onClear={() => upd({ color: undefined })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><span className={label}>Letter casing</span>
          <div className="flex gap-1">
            {[['none', 'Aa'], ['upper', 'AA'], ['lower', 'aa'], ['title', 'Ab']].map(([v, l]) => (
              <button key={v} onClick={() => upd({ casing: v })} className={`flex-1 h-9 rounded-lg border text-xs font-semibold ${(block.casing || 'none') === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'}`}>{l}</button>
            ))}
          </div></div>
        <div><span className={label}>Alignment</span><AlignBtns value={block.align || 'center'} onChange={(v) => upd({ align: v })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><span className={label}>Letter spacing</span>
          <input type="range" min="-2" max="8" step="0.5" value={block.letterSpacing ?? 0} onChange={(e) => upd({ letterSpacing: +e.target.value })} className="w-full" /></div>
        <div><span className={label}>Line height</span>
          <input type="range" min="0.9" max="2" step="0.05" value={block.lineHeight ?? 1.25} onChange={(e) => upd({ lineHeight: +e.target.value })} className="w-full" /></div>
      </div>
      <div>
        <span className={label}>Text effect</span>
        <div className="grid grid-cols-4 gap-1.5">
          {[['none', 'None'], ['shadow', 'Shadow'], ['outline', 'Outline'], ['glow', 'Glow'],
            ['lift', 'Lift'], ['foil', 'Gold foil'], ['holo', 'Holographic']].map(([v, l]) => (
            <button key={v} onClick={() => upd({ effect: v === 'none' ? undefined : v })}
              className={`h-8 rounded-lg border text-[10px] font-semibold ${(v === 'holo' ? 'inv-holo-text ' : v === 'foil' ? 'inv-foil-text ' : '')}${(block.effect || 'none') === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              data-testid={`text-effect-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Curve</span>
        <div className="grid grid-cols-3 gap-1.5">
          {[['none', 'Straight', undefined], ['arch', 'Arch ⌣', 60], ['frown', 'Arch ⌢', -60]].map(([v, l, val]) => {
            const on = v === 'none' ? !block.curve : v === 'arch' ? (block.curve || 0) > 0 : (block.curve || 0) < 0;
            return (
              <button key={v} onClick={() => upd({ curve: val })}
                className={`h-8 rounded-lg border text-[10px] font-semibold ${on ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
                data-testid={`text-curve-${v}`}>{l}</button>
            );
          })}
        </div>
        {block.curve ? (
          <div className="mt-2">
            <div className="flex items-center justify-between"><span className="text-[11px] text-muted-foreground">Curve amount</span><span className="text-[11px] text-muted-foreground">{Math.abs(block.curve)}</span></div>
            <input type="range" min="10" max="100" step="5"
              value={Math.abs(block.curve)}
              onChange={(e) => upd({ curve: Math.sign(block.curve) * +e.target.value })}
              className="w-full" data-testid="text-curve-amount" />
          </div>
        ) : null}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={addBox} className="flex-1 flex items-center justify-center gap-1.5 border border-dashed border-border rounded-lg py-2 text-sm font-semibold text-muted-foreground hover:text-primary"><Plus size={14} /> Add text box</button>
        <button onClick={removeBox} className="px-3 rounded-lg border border-border text-muted-foreground hover:text-accent-coral hover:border-accent-coral/40"><Trash size={15} /></button>
      </div>
    </div>
  );
}

function BackgroundTool({ invitation, patchDesign, token, flash }) {
  const d = invitation.design;
  const [g1, setG1] = useState(d.background?.from || d.colors?.bg || '#FBEFF1');
  const [g2, setG2] = useState(d.background?.to || d.colors?.accent || '#2563EB');
  const setSolid = (color) => patchDesign((dd) => { dd.colors = { ...dd.colors, bg: color }; dd.background = { type: 'solid', color }; dd.backdrop = null; return dd; });
  const setGrad = (from, to, angle = 150) => patchDesign((dd) => { dd.background = { type: 'gradient', from, to, angle }; dd.backdrop = null; return dd; });
  return (
    <div className="space-y-5">
      <div>
        <span className={label}>Color themes</span>
        <div className="flex flex-wrap gap-2">
          {PALETTES.map((p) => (
            <button key={p.key} title={p.name} onClick={() => patchDesign((dd) => { dd.colors = { ...p }; dd.background = { type: 'solid', color: p.bg }; return dd; })} className="w-8 h-8 rounded-full border-2 border-transparent hover:border-primary" style={{ background: p.accent }} />
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Fills & gradients</span>
        <div className="grid grid-cols-5 gap-2">
          {BG_PRESETS.map((bg, i) => (
            <button key={i} onClick={() => patchDesign((dd) => { dd.background = { ...bg }; if (bg.type === 'solid') dd.colors = { ...dd.colors, bg: bg.color }; dd.backdrop = null; return dd; })}
              className="h-12 rounded-lg border border-border" style={{ background: bg.type === 'gradient' ? `linear-gradient(150deg, ${bg.from}, ${bg.to})` : bg.color }} />
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Solid color · any shade</span>
        <ColorPicker value={d.background?.type === 'solid' ? (d.background.color || d.colors?.bg || '') : ''} onChange={setSolid} />
      </div>
      <div>
        <span className={label}>Build a gradient</span>
        <div className="rounded-xl border border-border p-2.5 space-y-2">
          <div className="h-10 rounded-lg border border-black/10" style={{ background: `linear-gradient(150deg, ${g1}, ${g2})` }} />
          <div className="grid grid-cols-2 gap-2">
            <ColorPicker value={g1} onChange={(c) => { setG1(c); setGrad(c, g2); }} />
            <ColorPicker value={g2} onChange={(c) => { setG2(c); setGrad(g1, c); }} />
          </div>
        </div>
      </div>
      <div>
        <span className={label}>Pattern</span>
        <div className="grid grid-cols-6 gap-1.5">
          {['none', 'dot', 'checker', 'grid', 'stripe', 'vstripe', 'wave', 'sunburst', 'terrazzo', 'bows', 'lace'].map((k) => (
            <button key={k} title={k}
              onClick={() => patchDesign((dd) => { const dc = { ...(dd.decor || {}) }; if (k === 'none') delete dc.pattern; else { dc.pattern = k; dc.patternColor = dc.patternColor || dd.colors?.accent; } dd.decor = dc; return dd; })}
              className={`h-9 rounded-lg border text-[9px] font-medium capitalize ${(d.decor?.pattern || 'none') === k ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              data-testid={`pattern-${k}`}>{k}</button>
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Border</span>
        <div className="grid grid-cols-6 gap-1.5">
          {['none', 'hairline', 'double', 'deco', 'arch', 'botanical'].map((k) => (
            <button key={k} title={k}
              onClick={() => patchDesign((dd) => { const dc = { ...(dd.decor || {}) }; if (k === 'none') delete dc.frame; else { dc.frame = k; dc.frameColor = dc.frameColor || dd.colors?.ink; } dd.decor = dc; return dd; })}
              className={`h-9 rounded-lg border text-[9px] font-medium capitalize ${(d.decor?.frame || 'none') === k ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              data-testid={`frame-${k}`}>{k}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={d.decor?.texture === 'grain'}
          onChange={(e) => patchDesign((dd) => { const dc = { ...(dd.decor || {}) }; if (e.target.checked) { dc.texture = 'grain'; dc.grainOpacity = dc.grainOpacity || 0.06; } else delete dc.texture; dd.decor = dc; return dd; })} />
        <span className="text-sm text-foreground">Paper grain texture</span>
      </label>
      <div>
        <span className={label}>Upload a background image</span>
        <DropZone kind="photo" accept="image/*" token={token} flash={flash} compact onUploaded={(url) => patchDesign((dd) => { dd.backdrop = url; return dd; })} />
        <input value={d.backdrop || ''} onChange={(e) => patchDesign((dd) => { dd.backdrop = e.target.value || null; return dd; })} placeholder="…or paste an image URL" className={field + ' mt-2'} />
        {d.backdrop && <button onClick={() => patchDesign((dd) => { dd.backdrop = null; return dd; })} className="text-[11px] text-muted-foreground hover:text-accent-coral mt-1.5">Remove background image</button>}
      </div>
    </div>
  );
}

const PARTICLE_CHOICES = [
  ['none', 'None', '—'], ['petals', 'Petals', '🌸'], ['confetti', 'Confetti', '🎊'],
  ['snow', 'Snow', '❄️'], ['bokeh', 'Bokeh', '🔮'], ['sparkle', 'Sparkle', '✨'],
  ['stars', 'Stars', '⭐'], ['hearts', 'Hearts', '💗'], ['balloons', 'Balloons', '🎈'], ['leaves', 'Leaves', '🍂'],
];

function EffectsTool({ invitation, patchDesign, premium }) {
  const cur = invitation.design.animation || 'none';
  const ambient = invitation.design.ambient || 'none';
  const particles = invitation.design.particles || 'none';
  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-muted-foreground mb-3">✨ Pick an animation that plays before the envelope opens.</p>
        <div className="grid grid-cols-2 gap-2">
          {REVEALS.map((r) => (
            <button key={r.key} onClick={() => patchDesign((d) => { d.animation = r.key; return d; })}
              className={`relative h-20 rounded-xl border-2 flex items-center justify-center text-sm font-semibold ${cur === r.key ? 'border-primary bg-primary/5 text-primary' : 'border-border text-foreground hover:border-primary/40'}`}>
              {r.label}
              {r.premium && <span className="absolute top-1.5 right-1.5"><Star size={11} weight="fill" className="text-accent-coral" /></span>}
            </button>
          ))}
        </div>
        {!premium && <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1"><Star size={11} weight="fill" className="text-accent-coral" /> Premium reveals apply with an event upgrade or credits at send.</p>}
      </div>

      <div>
        <span className={label}>Living background</span>
        <p className="text-[11px] text-muted-foreground mb-2">Soft color that gently drifts behind your card — makes it feel alive.</p>
        <div className="grid grid-cols-2 gap-2">
          {[['none', 'Off'], ['drift', 'Drift ✨']].map(([v, l]) => (
            <button key={v} onClick={() => patchDesign((d) => { d.ambient = v === 'none' ? undefined : v; return d; })}
              className={`h-10 rounded-lg border text-xs font-semibold ${ambient === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              data-testid={`ambient-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      <div>
        <span className={label}>Floating touches</span>
        <p className="text-[11px] text-muted-foreground mb-2">Drifting petals, confetti, snow &amp; more — matched to your event.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {PARTICLE_CHOICES.map(([v, l, e]) => (
            <button key={v} onClick={() => patchDesign((d) => { d.particles = v === 'none' ? undefined : v; return d; })}
              className={`h-9 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 ${particles === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              data-testid={`particles-${v}`}><span>{e}</span> {l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function OpeningTool({ invitation, patchDesign, onReplay }) {
  const cur = invitation.design.openFx || 'flap-unseal';
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3">🎬 Choose how your envelope opens. Pick one, then tap <span className="font-semibold text-foreground">Play</span> on the preview to watch it.</p>
      <div className="space-y-2">
        {TRANSITIONS.map((tr) => {
          const active = cur === tr.key;
          return (
            <button key={tr.key}
              onClick={() => { patchDesign((d) => { d.openFx = tr.key; return d; }); onReplay?.(); }}
              className={`w-full flex items-start gap-3 rounded-xl border-2 p-3 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
              data-testid={`transition-${tr.key}`}>
              <span className="text-xl leading-none mt-0.5" aria-hidden>{tr.emoji}</span>
              <span className="min-w-0">
                <span className={`block text-sm font-semibold ${active ? 'text-primary' : 'text-foreground'}`}>{tr.label}</span>
                <span className="block text-[11px] text-muted-foreground leading-snug">{tr.desc}</span>
              </span>
              {active && <Check size={16} weight="bold" className="text-primary ml-auto mt-0.5 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EnvelopeTool({ invitation, patchDesign }) {
  const [sub, setSub] = useState('colors');
  const d = invitation.design;
  return (
    <div>
      <div className="flex gap-1 mb-4 border-b border-border">
        {['colors', 'liners', 'stamps', 'address'].map((s) => (
          <button key={s} onClick={() => setSub(s)} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide capitalize border-b-2 -mb-px ${sub === s ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>{s}</button>
        ))}
      </div>
      {sub === 'address' && (
        <div className="space-y-4">
          <div>
            <span className={label}>Wax-seal initial</span>
            <input maxLength={2} value={d.envelope?.initial || ''} onChange={(e) => patchDesign((dd) => { dd.envelope = { ...dd.envelope, initial: e.target.value.toUpperCase() || undefined }; return dd; })}
              placeholder="e.g. E" className={field + ' text-center text-lg font-semibold'} data-testid="envelope-initial" />
            <p className="text-[11px] text-muted-foreground mt-1">Stamped into the wax seal. Leave blank for the ✦ mark.</p>
          </div>
          <div>
            <span className={label}>Recipient name on the envelope</span>
            <input value={d.envelope?.recipient || ''} onChange={(e) => patchDesign((dd) => { dd.envelope = { ...dd.envelope, recipient: e.target.value || undefined }; return dd; })}
              placeholder="e.g. The Smith Family" className={field} data-testid="envelope-recipient" />
            <p className="text-[11px] text-muted-foreground mt-1">Handwritten across the front, like a real addressed envelope.</p>
          </div>
        </div>
      )}
      {sub === 'colors' && (
        <div className="space-y-3">
          <div className="grid grid-cols-6 gap-2">
            {ENVELOPE_COLORS.map((c) => (
              <button key={c} onClick={() => patchDesign((dd) => { dd.envelope = { ...dd.envelope, color: c }; return dd; })} className={`w-9 h-9 rounded-full border-2 ${d.envelope?.color === c ? 'border-primary' : 'border-black/10'}`} style={{ background: c }} />
            ))}
          </div>
          <ColorPicker value={d.envelope?.color || ''} onChange={(c) => patchDesign((dd) => { dd.envelope = { ...dd.envelope, color: c }; return dd; })} />
        </div>
      )}
      {sub === 'liners' && (
        <div className="space-y-3">
          <div className="grid grid-cols-6 gap-2">
            {LINER_COLORS.map((c) => (
              <button key={c} onClick={() => patchDesign((dd) => { dd.liner = { ...dd.liner, color: c, pattern: dd.liner?.pattern === 'solid' ? 'floral' : (dd.liner?.pattern || 'floral') }; return dd; })} className={`w-9 h-9 rounded-full border-2 ${d.liner?.color === c ? 'border-primary' : 'border-black/10'}`} style={{ background: c }} />
            ))}
          </div>
          <ColorPicker value={d.liner?.color || ''} onChange={(c) => patchDesign((dd) => { dd.liner = { ...dd.liner, color: c, pattern: dd.liner?.pattern || 'floral' }; return dd; })} />
        </div>
      )}
      {sub === 'stamps' && (
        <div className="grid grid-cols-6 gap-2">
          {STAMPS.map((s) => (
            <button key={s} onClick={() => patchDesign((dd) => { dd.stamp = { ...dd.stamp, emoji: s }; return dd; })} className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center text-lg ${d.stamp?.emoji === s ? 'border-primary bg-primary/5' : 'border-border'}`}>{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const COVER_STYLE_LABELS = {
  cinematic: 'Cinematic 🎬', renaissance: 'Renaissance 🖼️', 'retro-poster': 'Retro poster 📻',
  anime: 'Anime ✨', watercolor: 'Watercolor 🎨', 'pop-art': 'Pop art 💥', '3d-render': '3D render 🧊',
  papercut: 'Paper-cut ✂️', 'y2k-chrome': 'Y2K chrome 🪩', festive: 'Festive 🎉',
};

// AI cover art — describe a hero image + pick a style; sets it as the invite photo.
function CoverArtPanel({ invitation, token, flash, onImage }) {
  const [avail, setAvail] = useState(null);      // null=loading, {configured, styles}
  const [subject, setSubject] = useState('');
  const [style, setStyle] = useState('cinematic');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { invitesApi.coverStyles().then(setAvail).catch(() => setAvail({ configured: false, styles: [] })); }, []);

  const styleKeys = (avail?.styles || []).map((s) => s.key);
  const go = async () => {
    const s = subject.trim();
    if (s.length < 3) { setErr('Describe your cover first.'); return; }
    setErr(''); setBusy(true);
    try {
      const r = await invitesApi.generateCover(invitation.invitation_id, { subject: s, style }, token);
      onImage(r.url);
      flash?.('Cover art created ✨');
    } catch (e) { setErr(e?.response?.data?.detail || 'Couldn’t create that — try again.'); }
    finally { setBusy(false); }
  };

  if (avail === null) return null;
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-3" data-testid="cover-art-panel">
      <div className="flex items-center gap-1.5 mb-1"><MagicWand size={15} weight="fill" className="text-primary" /><span className="text-sm font-semibold text-foreground">AI cover art</span></div>
      {!avail.configured ? (
        <p className="text-[11px] text-muted-foreground">Generate a stylized hero image from a description. <span className="text-foreground/70">Turn this on by adding an image-model API key to the backend.</span></p>
      ) : (
        <>
          <p className="text-[11px] text-muted-foreground mb-2">Describe your hero image and pick a style — we’ll paint it.</p>
          <textarea value={subject} rows={2} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. the birthday girl holding sparklers on a rooftop at dusk" className={field + ' resize-none'} data-testid="cover-subject" />
          <select value={style} onChange={(e) => setStyle(e.target.value)} className={field + ' mt-2'} data-testid="cover-style">
            {(styleKeys.length ? styleKeys : ['cinematic']).map((k) => <option key={k} value={k}>{COVER_STYLE_LABELS[k] || k}</option>)}
          </select>
          <button onClick={go} disabled={busy}
            className="w-full mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground py-2 text-sm font-semibold disabled:opacity-60" data-testid="cover-generate">
            {busy ? <><SpinnerGap size={14} className="animate-spin" /> Painting…</> : <><MagicWand size={14} weight="fill" /> Generate cover</>}
          </button>
          {err && <p className="text-[11px] text-accent-coral mt-1.5" data-testid="cover-error">{err}</p>}
        </>
      )}
    </div>
  );
}

function MediaTool({ invitation, patchDesign, token, flash }) {
  const d = invitation.design;
  const photos = d.photos || (d.photo ? [d.photo] : []);
  const setPhotos = (arr) => patchDesign((dd) => { dd.photos = arr; dd.photo = arr[0] || null; return dd; });
  const addPhoto = (url) => setPhotos([...(d.photos || (d.photo ? [d.photo] : [])), url]);
  const [photoUrl, setPhotoUrl] = useState('');
  return (
    <div className="space-y-5">
      <CoverArtPanel invitation={invitation} token={token} flash={flash} onImage={(url) => addPhoto(url)} />

      {/* Photos */}
      <div>
        <span className={label}>Photos</span>
        <DropZone kind="photo" accept="image/*" token={token} flash={flash} onUploaded={(url) => addPhoto(url)} />
        <div className="flex gap-2 mt-2">
          <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="…or paste an image URL" className={field} />
          <button onClick={() => { if (photoUrl.trim()) { addPhoto(photoUrl.trim()); setPhotoUrl(''); } }} className="px-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold shrink-0">Add</button>
        </div>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {photos.map((p, i) => (
              <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-border">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white w-4 h-4 flex items-center justify-center text-[10px] leading-none">×</button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-1.5">Add one or more — they play as a Ken-Burns slideshow at the top.</p>
      </div>

      {/* Photo filter */}
      {(photos.length > 0 || d.video) && (
        <div>
          <span className={label}>Photo filter</span>
          <div className="grid grid-cols-4 gap-1.5">
            {[['none', 'Original'], ['bw', 'B&W'], ['warm', 'Warm'], ['cool', 'Cool'], ['vintage', 'Vintage'], ['mono', 'Mono'], ['bright', 'Bright']].map(([v, l]) => (
              <button key={v} onClick={() => patchDesign((dd) => { dd.photoFilter = v === 'none' ? undefined : v; return dd; })}
                className={`h-8 rounded-lg border text-[10px] font-semibold ${(d.photoFilter || 'none') === v ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
                data-testid={`photo-filter-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      )}

      {/* Video */}
      <div>
        <span className={label}>Video <Star size={10} weight="fill" className="inline text-accent-coral" /></span>
        <DropZone kind="video" accept="video/*" token={token} flash={flash} compact onUploaded={(url) => patchDesign((dd) => { dd.video = url; return dd; })} />
        <input value={d.video || ''} onChange={(e) => patchDesign((dd) => { dd.video = e.target.value || null; return dd; })} placeholder="…or paste an MP4 URL" className={field + ' mt-2'} />
        {d.video && (
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-muted-foreground truncate flex items-center gap-1"><FilmSlate size={12} /> Video attached</span>
            <button onClick={() => patchDesign((dd) => { dd.video = null; return dd; })} className="text-[11px] text-muted-foreground hover:text-accent-coral">Remove</button>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-1">Plays muted on loop as the header. Premium extra.</p>
      </div>
    </div>
  );
}

function MusicTool({ invitation, patchDesign }) {
  const d = invitation.design;
  const music = (d.music && typeof d.music === 'object') ? d.music : (d.music ? { url: d.music, title: 'My song', artist: '' } : null);
  const setMusic = (m) => patchDesign((dd) => { dd.music = m; return dd; });
  const accent = d.colors?.accent || '#2563EB';
  return (
    <div>
      <span className={label}>Attach a song <Star size={10} weight="fill" className="inline text-accent-coral" /></span>
      <p className="text-xs text-muted-foreground mb-3">Adds a song that plays when guests open the invite — like an Instagram post with a track.</p>
      <div className="space-y-1.5">
        {SONG_LIBRARY.map((s) => (
          <button key={s.title} onClick={() => setMusic(s)}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border text-left transition-colors ${music?.url === s.url ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary/40'}`}>
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs shrink-0" style={{ background: accent }}><MusicNotes size={13} weight="fill" /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium text-foreground truncate">{s.title}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{s.artist} · {s.mood}</span>
            </span>
            {music?.url === s.url && <Check size={14} className="text-primary shrink-0" />}
          </button>
        ))}
      </div>
      <input className={field + ' mt-2'} placeholder="…or paste your own MP3 URL"
        value={music?.url && !SONG_LIBRARY.some((s) => s.url === music.url) ? music.url : ''}
        onChange={(e) => setMusic(e.target.value ? { url: e.target.value, title: music?.title || 'My song', artist: music?.artist || '' } : null)} />
      {music && <button onClick={() => setMusic(null)} className="text-[11px] text-muted-foreground hover:text-accent-coral mt-1.5">Remove song</button>}
      <p className="text-[10px] text-muted-foreground mt-2">Premium extra.</p>
    </div>
  );
}

// ══════════════ MAIN ══════════════
export default function InvitationsBuilderPage() {
  const { eventId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromBooking = params.get('from') === 'booking';
  const checkoutParam = params.get('checkout');

  const [event, setEvent] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [step, setStep] = useState('design');
  const [tool, setTool] = useState('text');
  const [selectedId, setSelectedId] = useState('title');
  const [replayKey, setReplayKey] = useState(0); // bump to replay the opening preview
  const [selSticker, setSelSticker] = useState(null);
  const [browsing, setBrowsing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const [templates, setTemplates] = useState([]);
  const [facets, setFacets] = useState(null);
  const [filters, setFilters] = useState({ format: '', category: '', color: '', premium: '', q: '' });
  const [guests, setGuests] = useState([]);
  const [paywall, setPaywall] = useState(null);
  const [pricing, setPricing] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const loadEvent = useCallback(async () => {
    const data = await invitesApi.getEvent(eventId, token);
    setEvent(data.event); setEntitlement(data.entitlement);
    if (data.invitations?.length) setInvitation(await invitesApi.getInvitation(data.invitations[0].invitation_id, token));
    setLoading(false);
  }, [eventId, token]);

  useEffect(() => { loadEvent().catch(() => setLoading(false)); }, [loadEvent]);
  useEffect(() => { invitesApi.pricing().then(setPricing).catch(() => {}); }, []);

  const showGallery = !invitation || browsing;
  useEffect(() => {
    if (!showGallery) return;
    const p = { limit: 24 };
    Object.entries(filters).forEach(([k, v]) => { if (v !== '') p[k] = v; });
    invitesApi.templates(p).then((d) => { setTemplates(d.templates); setFacets(d.facets); }).catch(() => {});
  }, [filters, showGallery]);

  useEffect(() => {
    if (!checkoutParam || checkoutParam === 'cancel') return;
    invitesApi.checkoutStatus(checkoutParam, token).then(() => { loadEvent(); flash('Upgrade applied — you’re all set.'); }).catch(() => {});
  }, [checkoutParam, token, loadEvent]);

  useEffect(() => {
    if (invitation && step === 'guests') invitesApi.guests(invitation.invitation_id, token).then((d) => setGuests(d.guests)).catch(() => {});
  }, [invitation, step, token]);

  const pickTemplate = async (t) => {
    if (invitation) {
      // swap design on the current invitation, keeping event text
      const design = applyEventToBlocks(designFromTemplate(t), invitation.event);
      const saved = await invitesApi.updateInvitation(invitation.invitation_id, { design }, token);
      setInvitation({ ...saved, template_premium: t.premium, format: t.format });
      // persist template flag/format server-side for correct enforcement
      invitesApi.updateInvitation(invitation.invitation_id, { template_id: t.template_id, format: t.format, template_premium: t.premium }, token).catch(() => {});
    } else {
      const inv = await invitesApi.createInvitation(eventId, { template_id: t.template_id, format: t.format }, token);
      setInvitation(inv);
    }
    setBrowsing(false); setSelectedId('title'); setStep('design'); flash('Design applied — customize it.');
  };

  // One-Prompt Party — describe the event, Claude designs the whole invite.
  const generateFromPrompt = async (prompt, fmt) => {
    const inv = await invitesApi.generateInvitation(eventId, { prompt, format: fmt || 'card' }, token);
    setInvitation(inv);
    setBrowsing(false); setSelectedId('title'); setStep('design');
    flash('Designed by AI ✨ — tweak anything you like.');
  };

  // Generate 3 distinct AI directions to choose from (Canva-style).
  const generateOptions = (prompt, fmt) => invitesApi.generateOptions(eventId, { prompt, format: fmt || 'card' }, token);
  const chooseOption = async (opt, prompt) => {
    const inv = await invitesApi.createFromSpec(eventId, { spec: opt.spec, format: opt.format, prompt }, token);
    setInvitation(inv);
    setBrowsing(false); setSelectedId('title'); setStep('design');
    flash('Nice pick ✨ — tweak anything you like.');
  };

  // Conversational refine — "more neon", "fancier", "funnier" → regenerate in place.
  const remixInvite = async (instruction) => {
    const saved = await invitesApi.remix(invitation.invitation_id, { instruction }, token);
    setInvitation((i) => ({ ...saved, template_premium: i.template_premium, format: saved.format || i.format }));
    setReplayKey((k) => k + 1);
    flash('Updated ✨');
  };

  const patchDesign = (updater) => setInvitation((inv) => ({ ...inv, design: updater({ ...inv.design }) }));

  const saveDraft = useCallback(async (silent) => {
    if (!invitation) return;
    setSaving(true);
    try {
      const registry_links = (invitation.registry_links || []).filter((r) => (r.url || '').trim());
      const saved = await invitesApi.updateInvitation(invitation.invitation_id, { design: invitation.design, event: invitation.event, questions: invitation.questions, registry_links, format: invitation.format }, token);
      setInvitation((i) => ({ ...saved, template_premium: i.template_premium, format: saved.format || i.format }));
      if (!silent) flash('Saved.');
    } finally { setSaving(false); }
  }, [invitation, token]);

  const gotoStep = async (s) => { await saveDraft(true); setStep(s); };

  const doSend = async (channels) => {
    try {
      const res = await invitesApi.send(invitation.invitation_id, { channels }, token);
      flash(`Sent to ${res.recipients} guest${res.recipients === 1 ? '' : 's'}.`);
      setInvitation((i) => ({ ...i, status: 'sent' }));
      if (fromBooking) setTimeout(() => navigate('/coordinator'), 1400);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && d) setPaywall(d); else flash(d?.message || d || 'Could not send.');
    }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-6xl mx-auto p-8"><div className="h-64 bg-secondary/50 rounded-2xl animate-pulse" /></div></div>;

  if (showGallery) {
    return (
      <div className="min-h-screen bg-background" data-testid="invitations-builder">
        <Navbar />
        <Gallery facets={facets} filters={filters} setFilters={setFilters} templates={templates} onPick={pickTemplate}
          onGenerate={generateFromPrompt} onGenerateOptions={generateOptions} onChooseOption={chooseOption}
          canClose={!!invitation} onClose={() => setBrowsing(false)} />
        {toast && <Toast>{toast}</Toast>}
      </div>
    );
  }

  const stepIdx = STEPS.findIndex((s) => s.key === step);
  const ToolPanel = { text: TextTool, palette: PaletteTool, stickers: StickersTool, background: BackgroundTool, media: MediaTool, music: MusicTool, effects: EffectsTool, envelope: EnvelopeTool, opening: OpeningTool }[tool];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="invitations-builder">
      <Navbar />

      {/* Top step bar */}
      <div className="sticky top-16 z-30 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => setBrowsing(true)} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground shrink-0"><ArrowLeft size={15} /> <span className="hidden sm:inline">Change design</span></button>
          {entitlement && <span className={`hidden sm:inline text-[11px] font-semibold px-2 py-0.5 rounded-full ${entitlement.premium ? 'bg-accent-coral/10 text-accent-coral' : 'bg-secondary text-secondary-foreground'}`}>{entitlement.source === 'pro' ? '👑 Pro' : entitlement.source === 'tier' ? `Up to ${entitlement.guest_cap}` : 'Free'}</span>}
          <div className="flex-1 flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto">
            {STEPS.map((s, i) => (
              <button key={s.key} onClick={() => gotoStep(s.key)} className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap ${step === s.key ? 'bg-primary text-primary-foreground' : i < stepIdx ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${step === s.key ? 'bg-white/25' : i < stepIdx ? 'bg-primary/15' : 'bg-secondary'}`}>{i < stepIdx ? '✓' : i + 1}</span>{s.label}
              </button>
            ))}
          </div>
          {step !== 'review' ? (
            <button onClick={() => gotoStep(STEPS[stepIdx + 1].key)} className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-4 sm:px-5 h-9 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 shrink-0" data-testid="builder-next">Next <ArrowRight size={15} /></button>
          ) : (
            <span className="text-xs text-muted-foreground shrink-0">{saving ? 'Saving…' : 'Ready to send'}</span>
          )}
        </div>
      </div>

      {/* ── DESIGN STEP: rail + panel + canvas ── */}
      {step === 'design' && (
        <div className="flex-1 flex min-h-[calc(100vh-7.5rem)]">
          {/* Icon rail */}
          <div className="w-16 sm:w-20 border-r border-border bg-card flex flex-col py-3 gap-1 shrink-0">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTool(t.key)} className={`flex flex-col items-center gap-1 py-2.5 mx-1.5 rounded-xl text-[10px] font-medium ${tool === t.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary/60'}`} data-testid={`tool-${t.key}`}>
                  <Icon size={20} weight={tool === t.key ? 'fill' : 'regular'} /><span className="leading-none text-center">{t.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
          {/* Tool panel */}
          <div className="w-[300px] sm:w-[340px] border-r border-border bg-card overflow-y-auto p-4 shrink-0" data-testid="tool-panel">
            <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">{TOOLS.find((t) => t.key === tool)?.label}</p>
            <ToolPanel invitation={invitation} patchDesign={patchDesign} selectedId={selectedId} setSelectedId={setSelectedId} premium={entitlement?.premium} token={token} flash={flash} onReplay={() => setReplayKey((k) => k + 1)} selectedStickerId={selSticker} setSelectedStickerId={setSelSticker} />
          </div>
          {/* Canvas */}
          <div className="flex-1 bg-secondary/30 flex flex-col items-center justify-center p-6 overflow-auto">
            <div className="w-full max-w-[560px] mb-3">
              <div className="flex gap-2 overflow-x-auto pb-1 sm:justify-center">
                {FORMATS.map((f) => (
                  <button key={f.key} onClick={() => setInvitation((i) => ({ ...i, format: f.key }))}
                    className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${invitation.format === f.key ? 'bg-primary text-primary-foreground shadow-soft' : 'bg-card border border-border text-muted-foreground hover:border-primary/40'}`} data-testid={`format-${f.key}`}>
                    <span>{f.emoji}</span> {f.label}
                  </button>
                ))}
              </div>
              {(() => { const h = FORMATS.find((f) => f.key === invitation.format)?.hint; return h ? <p className="text-[11px] text-muted-foreground text-center mt-1.5">💡 {h}</p> : null; })()}
            </div>
            {tool === 'envelope' ? (
              <EnvelopeView design={invitation.design} scale={320} />
            ) : tool === 'opening' ? (
              // Live opening preview — remounts (re-seals) when the effect changes
              // or the host taps a transition again, so they can watch it play.
              <InvitePreview key={`${invitation.design.openFx || 'flap-unseal'}-${replayKey}`}
                design={invitation.design} format={invitation.format} mode="interactive" scale={320} />
            ) : (
              <CardFace design={invitation.design} scale={340} format={invitation.format} rich editable
                selectedBlockId={selectedId} onSelectBlock={(id) => { setSelectedId(id); if (id) setTool('text'); }}
                selectedStickerId={selSticker}
                onSelectSticker={(id) => { setSelSticker(id); if (id) setTool('stickers'); }}
                onStickerPatch={(id, patch) => patchDesign((d) => { d.stickers = (d.stickers || []).map((s) => s.id === id ? { ...s, ...patch } : s); return d; })} />
            )}
            <p className="text-[11px] text-muted-foreground mt-4">{tool === 'envelope' ? 'Envelope preview' : tool === 'opening' ? '▶ Tap the envelope to watch it open' : 'Tap any text to edit · changes save as you go'}</p>
            <RefineBar onRemix={remixInvite} flash={flash} />
          </div>
        </div>
      )}

      {/* ── DETAILS STEP ── */}
      {step === 'details' && <DetailsStep invitation={invitation} setInvitation={setInvitation} onSave={saveDraft} />}

      {/* ── GUESTS STEP ── */}
      {step === 'guests' && <div className="max-w-5xl mx-auto w-full px-4 py-8"><GuestsTab invitation={invitation} guests={guests} setGuests={setGuests} token={token} flash={flash} /></div>}

      {/* ── REVIEW STEP ── */}
      {step === 'review' && <div className="max-w-5xl mx-auto w-full px-4 py-8"><SendTab invitation={invitation} setInvitation={setInvitation} guests={guests} entitlement={entitlement} token={token} doSend={doSend} flash={flash} fromBooking={fromBooking} navigate={navigate} /></div>}

      {paywall && pricing && <Paywall detail={paywall} pricing={pricing} eventId={eventId} token={token} onClose={() => setPaywall(null)} />}
      {toast && <Toast>{toast}</Toast>}
    </div>
  );
}

function Toast({ children }) {
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground text-background text-sm px-4 py-2.5 rounded-xl shadow-float z-[80] fade-up max-w-[90vw] text-center" data-testid="builder-toast">{children}</div>;
}

function DetailsStep({ invitation, setInvitation, onSave }) {
  const ev = invitation.event || {};
  const setEv = (patch) => setInvitation((i) => ({ ...i, event: { ...i.event, ...patch } }));
  const questions = invitation.questions || [];
  const setQ = (qs) => setInvitation((i) => ({ ...i, questions: qs }));
  const addQ = () => setQ([...questions, { id: `q${Date.now().toString(36)}`, label: 'New question', type: 'text', options: [], required: false }]);
  const registry = invitation.registry_links || [];
  const setReg = (r) => setInvitation((i) => ({ ...i, registry_links: r }));
  const addReg = () => setReg([...registry, { label: '', url: '' }]);

  // Details / second card + QR live inside the free-form design blob.
  const design = invitation.design || {};
  const dc = design.detailsCard || {};
  const dcItems = dc.items || [];
  const patchDC = (patch) => setInvitation((i) => ({ ...i, design: { ...i.design, detailsCard: { ...(i.design?.detailsCard || {}), ...patch } } }));
  const setItems = (items) => patchDC({ items });
  const addItem = () => setItems([...dcItems, { label: '', value: '' }]);
  const showQr = !!design.showQr;
  const toggleQr = () => setInvitation((i) => ({ ...i, design: { ...i.design, showQr: !i.design?.showQr } }));
  const detailsActive = dcItems.some((it) => (it.value || '').trim()) || showQr;
  const DETAIL_PRESETS = [
    { label: 'Address', value: '' }, { label: 'Parking', value: '' }, { label: 'Dress code', value: '' },
    { label: 'Schedule', value: '' }, { label: 'Notes', value: '' },
  ];

  return (
    <div className="max-w-3xl mx-auto w-full px-4 py-8 grid md:grid-cols-2 gap-6 items-start" data-testid="details-step">
      <div className="space-y-4">
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground">Event details</p>
          <div><span className={label}>Title</span><input value={ev.title || ''} onChange={(e) => setEv({ title: e.target.value })} className={field} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className={label}>Date</span><input type="date" value={ev.event_date || ''} onChange={(e) => setEv({ event_date: e.target.value })} className={field} /></div>
            <div><span className={label}>Time</span><input type="time" value={ev.start_time || ''} onChange={(e) => setEv({ start_time: e.target.value })} className={field} /></div>
          </div>
          <div><span className={label}>Location</span><input value={ev.location || ''} onChange={(e) => setEv({ location: e.target.value })} className={field} /></div>
          <div><span className={label}>Hosted by</span><input value={invitation.host_name || ''} onChange={(e) => setInvitation((i) => ({ ...i, host_name: e.target.value }))} className={field} /></div>
        </div>
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold text-foreground">Custom RSVP questions</p><button onClick={addQ} className="text-xs font-semibold text-primary flex items-center gap-1"><Plus size={13} /> Add</button></div>
          {questions.length === 0 && <p className="text-xs text-muted-foreground">Ask guests anything — meal choice, song requests, +1 names…</p>}
          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={q.id} className="border border-border rounded-lg p-2.5 space-y-2">
                <div className="flex gap-2">
                  <input value={q.label} onChange={(e) => setQ(questions.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className={field} />
                  <button onClick={() => setQ(questions.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-accent-coral"><Trash size={15} /></button>
                </div>
                <div className="flex gap-2">
                  <select value={q.type} onChange={(e) => setQ(questions.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} className="text-xs border border-border rounded px-2 py-1 bg-background">
                    <option value="text">Free response</option><option value="dropdown">Dropdown</option><option value="multiple_choice">Multiple choice</option><option value="yes_no">Yes / No</option>
                  </select>
                  {(q.type === 'dropdown' || q.type === 'multiple_choice') && (
                    <input value={(q.options || []).join(', ')} onChange={(e) => setQ(questions.map((x, j) => j === i ? { ...x, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } : x))} placeholder="Option A, Option B…" className={field + ' text-xs'} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold text-foreground">Registry &amp; gift links</p><button onClick={addReg} className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="add-registry"><Plus size={13} /> Add</button></div>
          {registry.length === 0 && <p className="text-xs text-muted-foreground">Add your registry, honeymoon fund, or a Venmo/PayPal link — shown as buttons on your invitation.</p>}
          <div className="space-y-2">
            {registry.map((r, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`registry-row-${i}`}>
                <input value={r.label || ''} onChange={(e) => setReg(registry.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label — e.g. Amazon Registry" className={field + ' flex-1'} />
                <input value={r.url || ''} onChange={(e) => setReg(registry.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className={field + ' flex-1'} />
                <button onClick={() => setReg(registry.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-accent-coral shrink-0" aria-label="Remove link"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Details / second card */}
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4" data-testid="details-card-editor">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-foreground">Details card</p>
            <button onClick={addItem} className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="add-detail"><Plus size={13} /> Add</button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">A matching second card for the practical stuff — address, parking, dress code, schedule. Shown to guests below your invite.</p>
          {dcItems.length === 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1">
              {DETAIL_PRESETS.map((p) => (
                <button key={p.label} onClick={() => setItems([...dcItems, p])} className="text-[11px] font-semibold border border-dashed border-border rounded-full px-2.5 py-1 text-muted-foreground hover:text-primary hover:border-primary/40" data-testid={`detail-preset-${p.label}`}>
                  + {p.label}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {dcItems.map((it, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`detail-row-${i}`}>
                <input value={it.label || ''} onChange={(e) => setItems(dcItems.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label" className={field + ' w-28 shrink-0'} />
                <textarea value={it.value || ''} rows={1} onChange={(e) => setItems(dcItems.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="e.g. 88 King St W · enter rear lot" className={field + ' flex-1 resize-none'} />
                <button onClick={() => setItems(dcItems.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-accent-coral shrink-0 mt-2" aria-label="Remove detail"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Scan-to-RSVP QR */}
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 flex items-center justify-between" data-testid="qr-toggle-card">
          <div className="pr-3">
            <div className="flex items-center gap-2"><QrCode size={17} className="text-primary" /><p className="text-sm font-semibold text-foreground">Scan-to-RSVP code</p></div>
            <p className="text-xs text-muted-foreground mt-0.5">Adds a QR code guests can scan to open your invite and RSVP — great for printed cards, posters or a screen at the party.</p>
          </div>
          <button onClick={toggleQr} role="switch" aria-checked={showQr} data-testid="qr-toggle"
            className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${showQr ? 'bg-primary' : 'bg-border'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${showQr ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        <button onClick={() => onSave(false)} className="w-full bg-card border border-border rounded-xl h-11 text-sm font-semibold hover:bg-secondary/60">Save details</button>
      </div>
      <div className="space-y-4 md:sticky md:top-36">
        <div className="bg-secondary/40 rounded-2xl border border-border/60 p-6 flex justify-center">
          <InvitePreview design={invitation.design} format={invitation.format} mode="static" scale={260} />
        </div>
        {detailsActive && (
          <div className="bg-secondary/40 rounded-2xl border border-border/60 p-6 flex flex-col items-center gap-2" data-testid="details-card-preview">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Details card</span>
            <DetailsFace design={invitation.design} event={invitation.event} scale={220}
              rsvpUrl={`${window.location.origin}/i/${invitation.custom_url || invitation.public_id || 'preview'}`} />
          </div>
        )}
      </div>
    </div>
  );
}

function GuestsTab({ invitation, guests, setGuests, token, flash }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [csv, setCsv] = useState('');
  const refresh = () => invitesApi.guests(invitation.invitation_id, token).then((d) => setGuests(d.guests));
  const add = async () => {
    if (!form.email && !form.phone) return flash('Add an email or phone.');
    const r = await invitesApi.addGuests(invitation.invitation_id, [form], token);
    if (r.added_count) { setForm({ name: '', email: '', phone: '' }); refresh(); flash('Guest added.'); }
    else flash(r.skipped?.[0]?.reason === 'duplicate' ? 'Already on the list.' : 'Invalid — check the email.');
  };
  const doImport = async () => {
    if (!csv.trim()) return;
    const r = await invitesApi.importGuests(invitation.invitation_id, csv, token);
    setCsv(''); refresh(); flash(`Imported ${r.added_count} guest${r.added_count === 1 ? '' : 's'}${r.skipped?.length ? ` · ${r.skipped.length} skipped` : ''}.`);
  };
  const del = async (gid) => { await invitesApi.deleteGuest(invitation.invitation_id, gid, token); refresh(); };

  // ── AI per-guest personal notes ──
  const [vibe, setVibe] = useState('');
  const [personalizing, setPersonalizing] = useState(false);
  const [editing, setEditing] = useState(null);   // guest_id being hand-edited
  const [editVal, setEditVal] = useState('');
  const namedCount = guests.filter((g) => (g.name || '').trim()).length;

  const personalizeAll = async () => {
    if (personalizing) return;
    if (!namedCount) return flash('Add guest names first, then personalize.');
    setPersonalizing(true);
    try {
      const r = await invitesApi.personalize(invitation.invitation_id, { vibe: vibe.trim() || undefined }, token);
      await refresh();
      flash(`Wrote a personal note for ${r.personalized} guest${r.personalized === 1 ? '' : 's'}.`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Couldn't write the notes — try again.");
    } finally { setPersonalizing(false); }
  };
  const saveNote = async (gid) => {
    try {
      await invitesApi.setGuestNote(invitation.invitation_id, gid, editVal.trim(), token);
      setGuests((gs) => gs.map((g) => g.guest_id === gid ? { ...g, note: editVal.trim() } : g));
    } catch { flash('Could not save the note.'); }
    setEditing(null);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="guests-tab">
      <div className="space-y-5">
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5"><Users size={16} className="text-primary" /> Add a guest</p>
          <div className="space-y-2">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className={field} />
            <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className={field} />
            <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone (for SMS / WhatsApp)" className={field} />
            <button onClick={add} className="w-full bg-primary text-primary-foreground rounded-lg h-10 text-sm font-semibold btn-anim hover:bg-primary/90">Add guest</button>
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5"><UploadSimple size={16} className="text-primary" /> Import from CSV / spreadsheet</p>
          <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} placeholder={"name,email,phone\nJordan,jordan@email.com,555-1234"} className={field + ' font-mono text-xs resize-none'} />
          <button onClick={doImport} className="w-full mt-2 bg-card border border-border rounded-lg h-9 text-sm font-semibold hover:bg-secondary/60">Import list</button>
        </div>
      </div>
      <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
        <p className="text-sm font-semibold text-foreground mb-3">Guest list · {guests.length}</p>

        {/* AI personal notes — the moat template apps can't match */}
        {namedCount > 0 && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 mb-3">
            <p className="text-[13px] font-semibold text-foreground flex items-center gap-1.5"><Sparkle size={14} weight="fill" className="text-primary" /> Personalize each invite</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">AI writes a warm one-line note for each guest — shown just for them when they open their invite.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={vibe} onChange={(e) => setVibe(e.target.value)} placeholder="Optional tone, e.g. playful, heartfelt" className={field + ' text-xs'} />
              <button onClick={personalizeAll} disabled={personalizing}
                className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-lg h-9 px-3 text-xs font-semibold btn-anim hover:bg-primary/90 disabled:opacity-60"
                data-testid="personalize-all">
                <Sparkle size={13} weight="fill" /> {personalizing ? 'Writing…' : `Personalize ${namedCount}`}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
          {guests.map((g) => (
            <div key={g.guest_id} className="px-3 py-2 rounded-lg bg-secondary/40">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0"><p className="text-sm font-medium text-foreground truncate">{g.name || g.email || g.phone}</p><p className="text-[11px] text-muted-foreground truncate">{g.email}{g.email_status === 'invalid' && <span className="text-accent-coral"> · invalid</span>}</p></div>
                <button onClick={() => del(g.guest_id)} className="text-muted-foreground hover:text-accent-coral shrink-0"><Trash size={14} /></button>
              </div>
              {/* Per-guest note: show it, or edit inline */}
              {editing === g.guest_id ? (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <input value={editVal} onChange={(e) => setEditVal(e.target.value)} maxLength={140} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') saveNote(g.guest_id); if (e.key === 'Escape') setEditing(null); }}
                    placeholder="A personal line for this guest…" className={field + ' text-xs py-1.5'} />
                  <button onClick={() => saveNote(g.guest_id)} className="text-xs font-semibold text-primary shrink-0">Save</button>
                </div>
              ) : g.note ? (
                <button onClick={() => { setEditing(g.guest_id); setEditVal(g.note); }} className="mt-1 w-full text-left group" data-testid={`guest-note-${g.guest_id}`}>
                  <span className="text-[11px] text-foreground/70 italic leading-snug">“{g.note}” <span className="not-italic text-primary opacity-0 group-hover:opacity-100 transition-opacity">edit</span></span>
                </button>
              ) : (g.name || '').trim() ? (
                <button onClick={() => { setEditing(g.guest_id); setEditVal(''); }} className="mt-1 text-[11px] text-primary/80 hover:text-primary">+ add a personal note</button>
              ) : null}
            </div>
          ))}
          {!guests.length && <p className="text-sm text-muted-foreground text-center py-8">No guests yet. Add or import.</p>}
        </div>
      </div>
    </div>
  );
}

function SendTab({ invitation, setInvitation, guests, entitlement, token, doSend, flash, fromBooking, navigate }) {
  const [channels, setChannels] = useState(['email']);
  const [customUrl, setCustomUrl] = useState(invitation.custom_url || '');
  const [share, setShare] = useState('');
  const toggle = (c) => setChannels((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);
  const recipients = guests.filter((g) => g.email || g.phone).length;
  const saveUrl = async () => {
    try { const s = await invitesApi.updateInvitation(invitation.invitation_id, { custom_url: customUrl }, token); setInvitation(s); flash('Custom link saved.'); }
    catch (e) { flash(e.response?.data?.detail || 'That link is taken.'); }
  };
  const getShare = async () => { const s = await invitesApi.send(invitation.invitation_id, { channels: ['link'] }, token).catch(() => null); if (s) setShare(window.location.origin + s.share_path); };
  const CH = [{ key: 'email', label: 'Email', icon: '✉️' }, { key: 'sms', label: 'Text / SMS', icon: '💬' }, { key: 'whatsapp', label: 'WhatsApp', icon: '🟢' }, { key: 'link', label: 'Shareable link', icon: '🔗' }];
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="send-tab">
      <div className="space-y-5">
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5"><PaperPlaneRight size={16} className="text-primary" /> How to send</p>
          <div className="grid grid-cols-2 gap-2">
            {CH.map((c) => (
              <button key={c.key} onClick={() => toggle(c.key)} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${channels.includes(c.key) ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground'}`}><span>{c.icon}</span> {c.label} {channels.includes(c.key) && <Check size={13} className="ml-auto text-primary" />}</button>
            ))}
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4">
          <p className="text-sm font-semibold text-foreground mb-2">Custom event link</p>
          <div className="flex gap-2"><span className="text-sm text-muted-foreground self-center">/i/</span><input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="maya-birthday" className={field} /><button onClick={saveUrl} className="px-3 rounded-lg bg-card border border-border text-sm font-semibold hover:bg-secondary/60">Save</button></div>
        </div>
        <button onClick={() => doSend(channels)} disabled={!channels.length} className="w-full bg-primary text-primary-foreground rounded-xl h-12 text-sm font-semibold btn-anim shadow-lift hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="send-btn"><PaperPlaneRight size={16} weight="fill" /> Send to {recipients} guest{recipients === 1 ? '' : 's'}</button>
        <button onClick={getShare} className="w-full text-sm font-semibold text-primary hover:underline">Just get a shareable link →</button>
        {share && <div className="bg-secondary/50 rounded-lg p-3 text-xs break-all">{share}</div>}
        {fromBooking && <button onClick={() => navigate('/coordinator')} className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"><CalendarCheck size={14} /> Skip to your event coordinator →</button>}
      </div>
      <div className="bg-secondary/40 rounded-2xl border border-border/60 p-6 flex flex-col items-center">
        <InvitePreview design={invitation.design} format={invitation.format} mode="static" scale={240} />
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">Sending as <span className="font-semibold text-foreground">{entitlement?.source === 'pro' ? 'Pro' : entitlement?.source === 'tier' ? `Up to ${entitlement.guest_cap}` : 'Free'}</span></p>
          {invitation.status === 'sent' && <Link to={`/events/${invitation.event_id}/invitations/rsvps`} className="text-sm font-semibold text-primary hover:underline">Track RSVPs →</Link>}
        </div>
      </div>
    </div>
  );
}

function Paywall({ detail, pricing, eventId, token, onClose }) {
  const err = detail.error;
  const go = async (fn, body) => { try { const r = await fn(body, token); window.location.href = r.url; } catch { /* noop */ } };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border/70 rounded-3xl shadow-float w-full max-w-md p-6 fade-up">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X size={18} /></button>
        <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3"><Lock size={20} weight="fill" /></span>
        <h3 className="text-xl font-display font-semibold text-foreground">{err === 'guest_cap_exceeded' ? 'Upgrade your guest count' : err === 'premium_template_locked' ? 'Unlock this premium design' : err === 'insufficient_credits' ? 'You need more credits' : 'Upgrade to send'}</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">{detail.message || 'Choose an option to continue.'}</p>
        {err === 'insufficient_credits' ? (
          <div className="space-y-2">{pricing.credit_packs.map((p) => (
            <button key={p.key} onClick={() => go(invitesApi.checkoutCredits, { pack_key: p.key, origin_url: window.location.origin })} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-border hover:border-primary/50 hover:bg-secondary/40"><span className="text-sm font-semibold text-foreground">{p.credits} credits</span><span className="text-sm text-primary font-semibold">{money(p.amount, pricing.currency)}</span></button>
          ))}</div>
        ) : (
          <div className="space-y-2">
            {pricing.event_tiers.map((t) => (
              <button key={t.key} onClick={() => go(invitesApi.checkoutTier, { event_id: eventId, tier_key: t.key, origin_url: window.location.origin })} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-border hover:border-primary/50 hover:bg-secondary/40"><span className="text-sm font-semibold text-foreground">{t.label}</span><span className="text-sm text-primary font-semibold">{money(t.amount, pricing.currency)}</span></button>
            ))}
            <button onClick={() => go(invitesApi.checkoutSubscription, { plan: 'annual', origin_url: window.location.origin })} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-primary/40 bg-primary/5"><span className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Star size={14} weight="fill" className="text-primary" /> Pro — unlimited events</span><span className="text-sm text-primary font-semibold">{money(pricing.pro.plans[0].amount, pricing.currency)}/yr</span></button>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground text-center mt-4">Secure checkout via Stripe · applies the moment you return.</p>
      </div>
    </div>
  );
}
